import { useEffect, useState } from "react";
import { notify, lastNotifyError } from "./notify";
import { computeEstimate, DEFAULT_RATECARD, DEFAULT_SERVICES, type EstimateConfig, type RateCard, type AdditionalService } from "./rates";
import { programFor, programForTypes, iftPackageFor } from "./program";
import { BRIEF_MILESTONES, milestonesReady, proposedMilestones, applyMilestoneDates } from "./milestones";
import { releasableStage, scheduleStage, stageStart } from "./drawingStages";
import { DEFAULT_MATRIX, normalizeMatrix, type RoleMatrix, type RoleKey, type Capability } from "./permissions";
import { confirmedPaid, isConfirmed, refreshStageSchedule, scheduleUntouched } from "./billing";
import type {
  Expense, ExpenseCategory,
  Project, Studio, Stage, StageKey, DesignSpace, FeedbackItem, Invoice, Quotation, Activity, Brief, TeamMember, TeamRole, Client, Engagement, StudioLinkRef,
  TaskStatus, ClientNotice, Consultant, ConsultantDiscipline, RemarkResponse, ConceptBoard, ClientContact,
  ChecklistItem, BriefChecklist,
  PaymentSchedule, PaymentMilestone, StageValuation, InvoicePayment, Deliverable,
  RecordKind, RecordRecipient,
} from "./types";

/* ---------------------------------------------------------------- helpers */
const uid = (p = "") => p + Math.random().toString(36).slice(2, 9);
function bumpRev(rev: string): string {
  const m = /^([A-Za-z]*)(\d+)$/.exec(rev || "P1");
  return m ? `${m[1]}${parseInt(m[2], 10) + 1}` : "P2";
}
/** Apply the billing's tax setting to a net figure, returning the invoice's
 *  money fields. When tax is off, the amount is simply the net and no tax
 *  fields are recorded. The rate is captured so a later toggle can't rewrite it. */
function taxed(net: number, b: PaymentSchedule): { amount: number; net?: number; taxRate?: number; taxLabel?: string } {
  const t = b.tax;
  if (!t?.enabled || !(t.rate > 0)) return { amount: net };
  return { amount: Math.round(net * (1 + t.rate)), net, taxRate: t.rate, taxLabel: t.label || "VAT" };
}
function moodSvg(a: string, b: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${b}"/></linearGradient></defs><rect width="400" height="300" fill="url(#g)"/></svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}
export const STAGE_ORDER: { key: StageKey; label: string; blurb: string }[] = [
  { key: "engagement",    label: "Engagement",        blurb: "Appointment, fees & terms agreed." },
  { key: "brief",         label: "Design Brief",      blurb: "Objectives, scope & spaces defined and approved." },
  { key: "concept",       label: "Concept Design",    blurb: "Initial direction & mood explored." },
  { key: "developed",     label: "Developed Design",  blurb: "Scheme resolved across all spaces." },
  { key: "presentation",  label: "Design Presentation", blurb: "Client review, approvals & comments." },
  { key: "documentation", label: "Documentation",     blurb: "Technical drawings & specifications." },
  { key: "construction",  label: "Construction Monitoring", blurb: "Site inspections, RFIs and quality on the works." },
  { key: "delivery",      label: "Delivery",          blurb: "Tender, completion & handover." },
];

/** Reconcile a stored stage list against STAGE_ORDER, preserving progress. */
function normalizeStages(stages: Stage[] | undefined): Stage[] {
  const by = new Map((stages ?? []).map((x) => [x.key, x]));
  return STAGE_ORDER.map((def) => {
    const had = by.get(def.key);
    return { ...def, status: had?.status ?? "upcoming", startedOn: had?.startedOn, completedOn: had?.completedOn };
  });
}

function buildStages(currentIndex: number): Stage[] {
  return STAGE_ORDER.map((s, i) => ({
    ...s,
    status: i < currentIndex ? "done" : i === currentIndex ? "active" : "upcoming",
    startedOn: i <= currentIndex ? datesByStage[i]?.[0] : undefined,
    completedOn: i < currentIndex ? datesByStage[i]?.[1] : undefined,
  }));
}
const datesByStage = [
  // one row per STAGE_ORDER entry
  ["2026-02-03", "2026-02-14"],
  ["2026-02-17", "2026-03-13"],
  ["2026-03-16", "2026-04-10"],
  ["2026-04-13", "2026-05-22"],
  ["2026-05-25", ""],
  ["", ""],
  ["", ""],
  ["", ""],
];

/* Detailed brief template used for new projects (with client-facing hints). */
export const BRIEF_TEMPLATE = [
  { title: "Vision", hint: "In a sentence or two — what should this place feel like to walk into?" },
  { title: "Objectives", hint: "What must the project achieve, and who is it for?" },
  { title: "Spaces & program", hint: "Which rooms/spaces do you need, and how should they work together?" },
  { title: "Style direction", hint: "Materials, palette, mood — what are you drawn to? What do you want to avoid?" },
  { title: "Priorities", hint: "What matters most? Name the non-negotiables in order." },
  { title: "Lifestyle & use", hint: "How will you live in or use the space day to day?" },
  { title: "Budget & timeline", hint: "Your budget range and any dates you're working toward." },
  { title: "Constraints", hint: "Site, structural, regulatory or other limits we should design around." },
];

/* ------------------------------------------------------------------- seed */
function seedProjects(): Project[] {
  const villa: Project = {
    id: "p-villa",
    name: "Ravel House",
    code: "ATL·2026·014",
    type: "Private Residence",
    location: "Georgetown, Guyana",
    area: "540 m²",
    budget: 285000,
    currency: "USD",
    floors: 2,
    surveyPlan: "yes",
    geo: { lat: 6.8013, lng: -58.1551 },
    cover: 26,
    createdOn: "2026-02-03",
    client: {
      id: "c-1", name: "Elena Ravel", company: "Ravel Family Office",
      email: "elena@ravel.co", phone: "+592 612 0044", avatarHue: 18, since: "2026-02-03", code: "RVL-7K2M9P4Q",
    },
    stages: buildStages(4), // presentation active
    brief: {
      updatedOn: "2026-03-13", status: "approved",
      approvedBy: "Elena Ravel", approvedOn: "2026-03-13",
      clientNote: "Beautifully captured. The emphasis on quiet materials is exactly right.",
      areaUnit: "sqft",
      spaces: [
        { id: "sp-1", name: "Entry / Foyer", qty: 1, area: 140, zone: "Circulation", note: "generous arrival, per plan" },
        { id: "sp-2", name: "Living Room", qty: 1, area: 360, zone: "Public", note: "principal living, north-lit" },
        { id: "sp-3", name: "Dining", qty: 1, area: 240, zone: "Public", note: "seats 8" },
        { id: "sp-4", name: "Kitchen + Pantry", qty: 1, area: 320, zone: "Public", note: "island kitchen + walk-in pantry" },
        { id: "sp-5", name: "Primary Suite", qty: 1, area: 420, zone: "Private", note: "bedroom + ensuite + walk-through wardrobe" },
        { id: "sp-6", name: "Children's Bedroom", qty: 2, area: 210, zone: "Private", note: "double, with storage" },
        { id: "sp-7", name: "Family Bathroom", qty: 1, area: 95, zone: "Private" },
        { id: "sp-8", name: "Study", qty: 1, area: 160, zone: "Private", note: "quiet, acoustically separated" },
        { id: "sp-9", name: "Garden Room", qty: 1, area: 280, zone: "Public", note: "glazed pavilion to the loggia" },
        { id: "sp-10", name: "Utility / Laundry", qty: 1, area: 85, zone: "Service" },
        { id: "sp-11", name: "Circulation & services", qty: 1, area: 360, zone: "Circulation", note: "~15% net-to-gross allowance" },
      ],
      sections: [
        { id: uid(), title: "Vision", body: "A calm, light-filled family home that feels tailored rather than decorated — warm minimalism grounded in natural stone, oak and lime plaster." },
        { id: uid(), title: "Scope", body: "Full interior architecture across three levels: entry, living, dining, kitchen, primary suite, two children's rooms, study and a garden room." },
        { id: uid(), title: "Priorities", body: "Natural light, acoustic calm, generous storage that disappears, and a material palette that ages gracefully." },
        { id: uid(), title: "Constraints", body: "Existing structural grid retained. Budget ceiling of $285k. Occupancy target before year end." },
      ],
    },
    concepts: [
      { id: uid(), title: "Quiet Materiality", note: "Lime plaster, honed travertine, smoked oak. Light as the primary finish.", hue: 30, tags: ["palette", "texture"] },
      { id: uid(), title: "The Long View", note: "Enfilade of spaces framing the garden — sightlines carry through the plan.", hue: 150, tags: ["plan", "light"] },
      { id: uid(), title: "Soft Threshold", note: "Blurring inside and out with a deep planted loggia and full-height glazing.", hue: 200, tags: ["facade"] },
    ],
    presentation: {
      id: "pr-1", title: "Developed Design — Client Review 01", issuedOn: "2026-05-25", status: "sent",
      spaces: [
        { id: uid(), name: "Living Room", option: "Scheme B — Warm Minimal", hue: 28,
          description: "Plaster walls, a monolithic travertine hearth and a low oak media wall. Layered, indirect lighting.",
          materials: ["Lime plaster", "Honed travertine", "Smoked oak", "Bouclé"], response: "approved",
          comment: "Love it. Can the hearth be a touch wider?", respondedOn: "2026-05-29" },
        { id: uid(), name: "Kitchen", option: "Scheme A — Stone & Steel", hue: 42,
          description: "Full-height stone island, integrated appliances, brushed steel accents and a concealed pantry.",
          materials: ["Travertine", "Brushed steel", "Oak"], response: "revise",
          comment: "Prefer warmer metal — brass instead of steel?", respondedOn: "2026-05-30" },
        { id: uid(), name: "Primary Suite", option: "Scheme B — Warm Minimal", hue: 15,
          description: "A serene retreat with a plaster headboard wall, walk-through wardrobe and a stone wet-room.",
          materials: ["Lime plaster", "Oak", "Limestone"], response: "pending" },
        { id: uid(), name: "Garden Room", option: "Scheme C — Green Threshold", hue: 145,
          description: "A glazed pavilion that opens fully to the loggia; terrazzo floor and planted edges.",
          materials: ["Terrazzo", "Steel glazing", "Planting"], response: "pending" },
      ],
    },
    schedule: [
      { id: uid(), title: "Kick-off & brief workshop", date: "2026-02-17", kind: "meeting", stage: "brief" },
      { id: uid(), title: "Brief approved", date: "2026-03-13", kind: "milestone", stage: "brief" },
      { id: uid(), title: "Concept presentation", date: "2026-04-08", kind: "meeting", stage: "concept" },
      { id: uid(), title: "Site survey — levels", date: "2026-04-21", kind: "site" },
      { id: uid(), title: "Developed design issued", date: "2026-05-25", kind: "milestone", stage: "presentation" },
      { id: uid(), title: "Client review 01", date: "2026-07-15", kind: "meeting", stage: "presentation" },
      { id: uid(), title: "Sign-off — developed design", date: "2026-07-24", kind: "deadline", stage: "presentation" },
      { id: uid(), title: "Documentation start", date: "2026-08-03", kind: "milestone", stage: "documentation" },
    ],
    program: [
      { id: uid(), name: "Confirm brief & fee agreement", phase: "Engagement", owner: "Studio", start: "2026-02-03", end: "2026-02-14", progress: 100, status: "done" },
      { id: uid(), name: "Spatial planning studies", phase: "Concept", owner: "M. Okonkwo", start: "2026-03-16", end: "2026-04-02", progress: 100, status: "done" },
      { id: uid(), name: "Material palette & mood", phase: "Concept", owner: "L. Serra", start: "2026-03-20", end: "2026-04-08", progress: 100, status: "done" },
      { id: uid(), name: "Developed design — all spaces", phase: "Developed", owner: "Studio", start: "2026-04-13", end: "2026-05-22", progress: 100, status: "done" },
      { id: uid(), name: "Kitchen revision — brass", phase: "Presentation", owner: "L. Serra", start: "2026-07-11", end: "2026-07-18", progress: 35, status: "doing" },
      { id: uid(), name: "Lighting design coordination", phase: "Presentation", owner: "R. Devi", start: "2026-07-14", end: "2026-07-28", progress: 10, status: "doing" },
      { id: uid(), name: "Joinery package", phase: "Documentation", owner: "M. Okonkwo", start: "2026-08-03", end: "2026-08-28", progress: 0, status: "todo" },
      { id: uid(), name: "Structural sign-off — hearth", phase: "Documentation", owner: "Ext. Engineer", start: "2026-08-05", end: "2026-08-12", progress: 0, status: "blocked" },
    ],
    deliverables: iftPackageFor("Private Residence", 2).map((d) => ({
      ...d,
      status: d.phase !== "IFT" ? "issued" : (["A-010", "A-110", "A-310"].includes(d.code) ? "doing" : "todo"),
      issuedOn: d.phase !== "IFT" ? "2026-05-22" : undefined,
    })),
    reports: [
      { id: uid(), title: "Progress Report — May", period: "May 2026", issuedOn: "2026-06-01", status: "issued",
        summary: "Developed design completed across all four primary spaces and issued for client review. Concept sign-off achieved on schedule.",
        highlights: ["Developed design issued 25 May", "Material palette locked", "Kitchen scheme flagged for revision"] },
      { id: uid(), title: "Progress Report — June", period: "June 2026", issuedOn: "2026-07-02", status: "issued",
        summary: "Client review scheduled. Living room and primary suite tracking to approval; kitchen entering a warm-metal revision.",
        highlights: ["Living room approved", "Kitchen revision scoped", "Documentation phase mobilising"] },
    ],
    quotations: [
      { id: uid(), ref: "Q-014-A", title: "Interior Architecture — Full Service", issuedOn: "2026-02-05", status: "accepted", taxRate: 0.14,
        note: "Staged fee across RIBA-equivalent work stages.",
        lines: [
          { id: uid(), label: "Concept & developed design", qty: 1, unit: "stage", rate: 42000 },
          { id: uid(), label: "Technical documentation", qty: 1, unit: "stage", rate: 38000 },
          { id: uid(), label: "Construction support", qty: 1, unit: "stage", rate: 26000 },
        ] },
      { id: uid(), ref: "Q-014-B", title: "Bespoke Joinery — Design & Detailing", issuedOn: "2026-06-20", status: "sent", taxRate: 0.14,
        lines: [
          { id: uid(), label: "Kitchen & pantry joinery", qty: 1, unit: "package", rate: 14500 },
          { id: uid(), label: "Wardrobe & dressing", qty: 3, unit: "rooms", rate: 3200 },
        ] },
    ],
    invoices: [
      { id: uid(), ref: "INV-014-01", title: "Stage 1 — Concept & developed design", amount: 21000, issuedOn: "2026-02-14", dueOn: "2026-02-28", status: "paid", paidOn: "2026-02-25" },
      { id: uid(), ref: "INV-014-02", title: "Stage 1 — balance", amount: 21000, issuedOn: "2026-04-15", dueOn: "2026-04-30", status: "paid", paidOn: "2026-05-02" },
      { id: uid(), ref: "INV-014-03", title: "Stage 2 — documentation (deposit)", amount: 19000, issuedOn: "2026-06-28", dueOn: "2026-07-12", status: "due" },
      { id: uid(), ref: "INV-014-04", title: "Bespoke joinery — deposit", amount: 8100, issuedOn: "2026-06-22", dueOn: "2026-07-06", status: "overdue" },
      { id: uid(), ref: "INV-014-05", title: "Stage 2 — balance", amount: 19000, issuedOn: "", dueOn: "2026-09-01", status: "scheduled" },
    ],
    feedback: [
      { id: uid(), author: "client", authorName: "Elena Ravel", context: "Kitchen — Scheme A",
        body: "The kitchen feels a little cool to me. Could we explore brass instead of the brushed steel? Everything else is wonderful.",
        createdOn: "2026-05-30T09:20:00", resolved: false,
        replies: [{ id: uid(), author: "studio", authorName: "Studio", body: "Absolutely — brass warms it beautifully. We'll issue a revised board this week.", createdOn: "2026-05-30T11:05:00" }] },
      { id: uid(), author: "client", authorName: "Elena Ravel", context: "Living Room — Scheme B",
        body: "Can the travertine hearth be a touch wider? It wants to feel a bit more monolithic.",
        createdOn: "2026-05-29T18:40:00", resolved: false, replies: [] },
      { id: uid(), author: "client", authorName: "Elena Ravel", context: "General",
        body: "Thank you for the June report — very clear. Looking forward to the review.",
        createdOn: "2026-07-02T08:15:00", resolved: true, replies: [] },
    ],
    activity: [
      { id: uid(), on: "2026-07-02T08:15:00", who: "Elena Ravel", what: "acknowledged the June progress report", kind: "report" },
      { id: uid(), on: "2026-05-30T09:20:00", who: "Elena Ravel", what: "requested a revision on the Kitchen scheme", kind: "feedback" },
      { id: uid(), on: "2026-05-29T18:40:00", who: "Elena Ravel", what: "approved the Living Room design", kind: "design" },
      { id: uid(), on: "2026-05-25T16:00:00", who: "Studio", what: "issued Developed Design for review", kind: "design" },
      { id: uid(), on: "2026-03-13T14:00:00", who: "Elena Ravel", what: "approved the Design Brief", kind: "brief" },
    ],
    reviews: [
      { id: "rv-1", title: "Developed Design — Review 01", createdOn: "2026-05-26", status: "open", sharedWithClient: true, access: [],
        summary: undefined, sheets: [
          { id: "rs-1", code: "A-101", title: "Ground floor plan", discipline: "Architecture", revision: "P2", hue: 28, remarks: [
            { id: uid(), author: "client", authorName: "Elena Ravel", body: "Can the kitchen island shift 300mm toward the window? It feels tight against the pantry.", createdOn: "2026-05-29T10:00:00", resolved: false, onRevision: "P2" },
            { id: uid(), author: "team", authorName: "L. Serra", body: "Noted — will test the clearance and reissue.", createdOn: "2026-05-29T14:00:00", resolved: false, onRevision: "P2" },
          ] },
          { id: "rs-2", code: "A-200", title: "Elevations", discipline: "Architecture", revision: "P1", hue: 40, remarks: [
            { id: uid(), author: "client", authorName: "Elena Ravel", body: "Love the north elevation. Could the window heads align across the two bays?", createdOn: "2026-05-30T09:00:00", resolved: false, onRevision: "P1" },
          ] },
        ] },
    ],
    moodboard: {
      images: [
        { id: uid(), src: moodSvg("#e8e2d6", "#c9bda6"), caption: "Warm plaster & light", addedBy: "client", addedByName: "Elena Ravel", addedOn: "2026-03-20" },
        { id: uid(), src: moodSvg("#6b5842", "#a88c63"), caption: "Smoked oak tones", addedBy: "client", addedByName: "Elena Ravel", addedOn: "2026-03-20" },
        { id: uid(), src: moodSvg("#d6cbb6", "#b6a88a"), caption: "Honed travertine", addedBy: "team", addedByName: "L. Serra", addedOn: "2026-03-22" },
      ],
      swatches: [
        { id: uid(), name: "Lime plaster", hex: "#e8e2d6", material: "Plaster", note: "Primary wall finish" },
        { id: uid(), name: "Smoked oak", hex: "#6b5842", material: "Timber", note: "Joinery & floors" },
        { id: uid(), name: "Honed travertine", hex: "#d6cbb6", material: "Stone", note: "Hearth & wet areas" },
        { id: uid(), name: "Bouclé", hex: "#d9d2c4", material: "Textile", note: "Soft furnishings" },
      ],
    },
    consultants: [
      { id: uid(), discipline: "Structural", firm: "Meridian Structures", contact: "D. Alleyne", email: "hello@meridian-se.com", phone: "+5926041188", note: "Engaged for the timber frame and hearth structure.", status: "appointed", appointedBy: "client", appointedOn: "2026-06-30T10:00:00.000Z" },
    ],
  };

  const loft: Project = {
    ...villa,
    id: "p-loft", name: "Meridian Loft", code: "ATL·2026·019", type: "Apartment · Renovation",
    location: "Miami, FL", area: "210 m²", budget: 140000, cover: 205, createdOn: "2026-04-18",
    client: { id: "c-2", name: "Julian Vance", company: "Vance Studio", email: "jv@vance.studio", avatarHue: 205, since: "2026-04-18", code: "MRD-X4N8QJ2P" },
    stages: buildStages(2),
    brief: { ...villa.brief, status: "approved", approvedBy: "Julian Vance", approvedOn: "2026-05-06",
      sections: villa.brief.sections.slice(0, 3), clientNote: undefined },
    presentation: { ...villa.presentation, status: "draft", issuedOn: "", spaces: villa.presentation.spaces.slice(0, 2).map(s => ({ ...s, id: uid(), response: "pending", comment: undefined, respondedOn: undefined })) },
    invoices: [
      { id: uid(), ref: "INV-019-01", title: "Concept design deposit", amount: 12000, issuedOn: "2026-04-20", dueOn: "2026-05-04", status: "paid", paidOn: "2026-05-01" },
      { id: uid(), ref: "INV-019-02", title: "Concept design balance", amount: 12000, issuedOn: "2026-06-25", dueOn: "2026-07-09", status: "due" },
    ],
    feedback: [], activity: villa.activity.slice(3),
  };

  const office: Project = {
    ...villa,
    id: "p-office", name: "Atrium Offices", code: "ATL·2026·009", type: "Workplace", location: "Austin, TX",
    area: "1,240 m²", budget: 620000, cover: 150, createdOn: "2026-01-12",
    client: { id: "c-3", name: "Priya Nair", company: "Northwind Capital", email: "priya@northwind.vc", avatarHue: 150, since: "2026-01-12", code: "ATR-9J3KP7RM" },
    stages: buildStages(5),
    presentation: { ...villa.presentation, status: "approved", spaces: villa.presentation.spaces.map(s => ({ ...s, id: uid(), response: "approved", comment: undefined })) },
    invoices: [
      { id: uid(), ref: "INV-009-03", title: "Documentation — stage payment", amount: 48000, issuedOn: "2026-06-15", dueOn: "2026-06-30", status: "paid", paidOn: "2026-06-27" },
      { id: uid(), ref: "INV-009-04", title: "Documentation — balance", amount: 48000, issuedOn: "2026-07-01", dueOn: "2026-07-20", status: "due" },
    ],
    feedback: [], activity: villa.activity.slice(2),
  };

  return [villa, loft, office];
}

/* ---------------------------------------------------------------- studio */
export const STUDIO: Studio = {
  name: "HD Studio",
  tagline: "Architecture, interiors & visualization — made to measure.",
  principal: "R. Ferdinand",
  email: "renauto.ferdinand@hddesignstudiogy.com",
  domain: "studiogy.design",
};
export const MANAGER_CODE = "HDSTUDIO";

export const DEFAULT_LIMITATIONS = [
  "Fees exclude VAT, statutory/authority fees, and reimbursable expenses (printing, travel, courier).",
  "Structural, MEP and any specialist consultant fees are as scheduled and billed at their stated rates.",
  "3D renders beyond those included in the fee are charged per image (exterior/interior) as scheduled.",
  "Site visits are charged per visit; unscheduled visits at the stated premium.",
  "Design variations after the first two minor changes are charged per change; major variations per change.",
  "Local-authority approvals, permits and third-party sign-offs are obtained on the client's behalf at the client's cost.",
  "Construction, contractor procurement and the works themselves are outside this appointment.",
  "The programme assumes timely client decisions, information and payments.",
];
export const DEFAULT_ENGAGEMENT_TERMS =
  "This letter of engagement sets out the professional design services HD Studio will provide, the fee, the stages of work and the terms on which we are appointed. Fees are payable stage by stage per the payment schedule. Either party may suspend or terminate on written notice, with fees due for work completed and committed. Intellectual property in the design remains with HD Studio until fees are settled. By signing below, the client accepts this appointment, its scope and its limitations.";

export function defaultEngagement(): Engagement {
  return { status: "draft", limitations: [...DEFAULT_LIMITATIONS], terms: DEFAULT_ENGAGEMENT_TERMS };
}

function seedTeam(): TeamMember[] {
  return [
    { id: "t-aa", name: "A. Mensah",   email: "a.mensah@hddesignstudiogy.com",   role: "assistant", code: "STUDIO-AM", status: "active", avatarHue: 40 },
    { id: "t-ls", name: "L. Serra",    email: "l.serra@hddesignstudiogy.com",    role: "lead",     code: "STUDIO-LS", status: "active", avatarHue: 24 },
    { id: "t-mo", name: "M. Okonkwo",  email: "m.okonkwo@hddesignstudiogy.com",  role: "designer", code: "STUDIO-MO", status: "active", avatarHue: 150 },
    { id: "t-rd", name: "R. Devi",     email: "r.devi@hddesignstudiogy.com",     role: "viewer",   code: "STUDIO-RD", status: "invited", avatarHue: 205 },
  ];
}

/* --------------------------------------------------------- reactive store */
type State = { projects: Project[]; team: TeamMember[]; roleMatrix: RoleMatrix; rateCard: RateCard; templates?: import("./types").ProjectTemplate[]; leads?: import("./types").Lead[]; notifyPrefs?: Record<string, import("./types").NotifyPrefs> };
const KEY = "atelier.state.v2";

/** A random id that also works on Safari builds without crypto.randomUUID. */
function safeUUID(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(b); else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Fill in rate-card fields added after a workspace was first saved (e.g. the
 *  out-of-scope `services` schedule) so older stored/synced state stays valid. */
function normalizeRateCard(rc: RateCard | undefined): RateCard {
  if (!rc) return DEFAULT_RATECARD;
  return {
    ...rc,
    services: rc.services?.length ? rc.services : DEFAULT_SERVICES,
    buildRates: rc.buildRates?.length ? rc.buildRates : DEFAULT_RATECARD.buildRates,
  };
}

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (!s.team) s.team = seedTeam();
      s.roleMatrix = normalizeMatrix(s.roleMatrix);
      // a stage added after a project was created (construction monitoring)
      // appears as upcoming; everything already recorded is kept.
      s.projects = (s.projects ?? []).map((p: Project) => ({ ...p, stages: normalizeStages(p.stages) }));
      s.rateCard = normalizeRateCard(s.rateCard);
      return s;
    }
  } catch {}
  return { projects: seedProjects(), team: seedTeam(), roleMatrix: DEFAULT_MATRIX, rateCard: DEFAULT_RATECARD };
}

let state: State = load();
const subs = new Set<() => void>();
let applyingRemote = false;
let pushHook: (() => void) | null = null;
/* A payment schedule built from the fee stages follows the fee. While nothing
   has been invoiced against it, a change to the budget or the scope rebuilds
   it on the spot; once something is billed it is left alone and the studio is
   shown that it has drifted. */
function followFee(p: Project, rc: RateCard = state.rateCard): Project {
  if (!p.billing || p.billing.source !== "stages" || !scheduleUntouched(p.billing)) return p;
  const next = refreshStageSchedule(p, rc);
  return next ? { ...p, billing: next } : p;
}
function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  subs.forEach((f) => f());
  if (!applyingRemote) pushHook?.();
}
/** Replace state from the server without triggering a push back up. */
/* The workspace arrives from D1 whole, so it has to be brought up to the current
   shape here too — otherwise a stage added later never reaches a live project. */
export function applyRemote(next: State) {
  applyingRemote = true;
  const rc = normalizeRateCard(next.rateCard);
  commit({
    ...next,
    rateCard: rc,
    projects: (next.projects ?? []).map((p) => followFee({ ...p, stages: normalizeStages(p.stages) }, rc)),
  });
  applyingRemote = false;
}
/** Register a debounced push (called after every local mutation). */
export function setPushHook(fn: (() => void) | null) { pushHook = fn; }
export function getState(): State { return state; }
export function resetStore() { commit({ projects: seedProjects(), team: seedTeam(), roleMatrix: DEFAULT_MATRIX, rateCard: DEFAULT_RATECARD }); }
export function useTeam(): TeamMember[] { return useStore().team; }
export function useRoleMatrix(): RoleMatrix { return useStore().roleMatrix; }
export function useRateCard(): RateCard { return useStore().rateCard; }
export function snapshot(): State { return state; }

export function useStore(): State {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state;
}
export function useProject(id: string | undefined): Project | undefined {
  const s = useStore();
  return s.projects.find((p) => p.id === id);
}

/* --------------------------------------------------------------- mutators */
function patchProject(id: string, fn: (p: Project) => Project) {
  commit({ ...state, projects: state.projects.map((p) => (p.id === id ? fn(p) : p)) });
}
function logActivity(p: Project, who: string, what: string, kind: Activity["kind"]): Project {
  const a = { id: uid(), on: nowISO(), who, what, kind };
  return { ...p, activity: [a, ...p.activity].slice(0, 40) };
}
/** The real moment — activity, notices and timestamps are stamped when they
 *  actually happen (this was once a frozen 2026-07-10 demo clock, which stamped
 *  every real event two weeks stale). */
function nowISO() { return new Date().toISOString(); }
/** The real calendar day (date-only) — for fields that are dates, not instants. */
function realToday() { return new Date().toISOString().slice(0, 10); }

export const actions = {
  /* ---- documents of record -------------------------------------------
     A formal event is written to the project, then emailed to both parties
     by the worker (which composes the document from the record itself). The
     per-party outcome lands back on the record, so the UI can say whether
     the email went and offer to send it again. */
  addRecord(pid: string, rec: { kind: RecordKind; refId?: string; title: string; by: string; attachment?: { url: string; name: string } }): string {
    const id = uid("rec-");
    patchProject(pid, (p) => ({
      ...p,
      records: [{ id, ...rec, on: nowISO(), recipients: [
        { party: "client" as const, to: p.client.email || "", result: "pending" as const },
        { party: "studio" as const, to: STUDIO.email, result: "pending" as const },
      ] }, ...(p.records ?? [])].slice(0, 300),
    }));
    return id;
  },
  async sendRecord(pid: string, recordId: string): Promise<void> {
    const p = state.projects.find((x) => x.id === pid);
    const rec = p?.records?.find((r) => r.id === recordId);
    if (!p || !rec) return;
    const mark = (recipients: RecordRecipient[]) => patchProject(pid, (x) => ({ ...x, records: (x.records ?? []).map((r) => r.id === recordId ? { ...r, recipients } : r) }));
    mark(rec.recipients.map((r) => ({ ...r, result: "pending" as const, error: undefined })));
    const fail = (error: string) => mark(rec.recipients.map((r) => ({ ...r, result: "failed" as const, error, on: nowISO() })));
    try {
      const res = await fetch("/api/records/send", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pid, code: p.client.code, kind: rec.kind, refId: rec.refId, title: rec.title, by: rec.by, on: rec.on, attachment: rec.attachment }),
      });
      const d = (await res.json().catch(() => ({}))) as { results?: RecordRecipient[]; error?: string };
      if (!res.ok || !d.results) { fail(d.error ?? `HTTP ${res.status}`); return; }
      mark(d.results.map((r) => ({ ...r, on: nowISO() })));
    } catch (e: any) { fail(String(e?.message || e).slice(0, 160)); }
  },
  /** Write the record and send it — the one call every formal event makes. */
  record(pid: string, rec: { kind: RecordKind; refId?: string; title: string; by: string; attachment?: { url: string; name: string } }): string {
    const id = actions.addRecord(pid, rec);
    void actions.sendRecord(pid, id);
    return id;
  },

  /* ---- expenses: logged fast, approved, billed back on their own invoice ---- */
  logExpense(pid: string, d: { date: string; category: ExpenseCategory; description: string; amount: number; vendor?: string; paidBy: string; markupPercent?: number; reimbursable?: boolean; receipt?: { url: string; name: string }; note?: string; by: string }): string {
    const id = uid("exp-");
    patchProject(pid, (p) => {
      const rows = p.expenses ?? [];
      const n = rows.reduce((m, x) => Math.max(m, parseInt(x.ref.split("-")[1] ?? "0", 10) || 0), 0) + 1;
      const e: Expense = {
        id, ref: `EXP-${String(n).padStart(3, "0")}`, date: d.date, category: d.category, description: d.description.trim(), amount: Math.round(d.amount * 100) / 100,
        vendor: d.vendor?.trim() || undefined, paidBy: d.paidBy, markupPercent: d.markupPercent || undefined, reimbursable: d.reimbursable ?? true,
        receipt: d.receipt, note: d.note?.trim() || undefined, status: "logged", loggedBy: d.by, loggedOn: nowISO(),
      };
      return logActivity({ ...p, expenses: [e, ...rows] }, d.by, `logged expense ${e.ref} — ${e.description} (${money(e.amount, p.currency)})`, "payment");
    });
    return id;
  },
  updateExpense(pid: string, id: string, d: Partial<Pick<Expense, "date" | "category" | "description" | "amount" | "vendor" | "paidBy" | "markupPercent" | "reimbursable" | "note" | "receipt">>) {
    patchProject(pid, (p) => ({ ...p, expenses: (p.expenses ?? []).map((e) => e.id === id && e.status !== "billed" && e.status !== "reimbursed" ? { ...e, ...d } : e) }));
  },
  approveExpense(pid: string, id: string, by: string) {
    patchProject(pid, (p) => ({ ...p, expenses: (p.expenses ?? []).map((e) => e.id === id && e.status === "logged" ? { ...e, status: "approved" as const, approvedBy: by, approvedOn: nowISO(), rejectedNote: undefined } : e) }));
  },
  rejectExpense(pid: string, id: string, note?: string) {
    patchProject(pid, (p) => ({ ...p, expenses: (p.expenses ?? []).map((e) => e.id === id && (e.status === "logged" || e.status === "approved") ? { ...e, status: "rejected" as const, rejectedNote: note?.trim() || undefined } : e) }));
  },
  reopenExpense(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, expenses: (p.expenses ?? []).map((e) => e.id === id && (e.status === "rejected" || e.status === "approved") ? { ...e, status: "logged" as const, approvedBy: undefined, approvedOn: undefined, rejectedNote: undefined } : e) }));
  },
  removeExpense(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, expenses: (p.expenses ?? []).filter((e) => e.id !== id || e.status === "billed" || e.status === "reimbursed") }));
  },
  /** One invoice for the approved reimbursables — each receipt its own line,
   *  tax applied the way the project's billing says, due on the usual terms. */
  invoiceExpenses(pid: string, ids: string[], by: string): string | null {
    let invoiceId: string | null = null;
    patchProject(pid, (p) => {
      const pick = (p.expenses ?? []).filter((e) => ids.includes(e.id) && e.status === "approved" && e.reimbursable && !e.invoiceId);
      if (!pick.length) return p;
      const billed = (e: Expense) => Math.round(e.amount * (1 + (e.markupPercent ?? 0) / 100) * 100) / 100;
      const net = Math.round(pick.reduce((s, e) => s + billed(e), 0) * 100) / 100;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (p.billing?.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const inv: Invoice = {
        id: uid("inv-"), ref: `INV-${tail}-${seq}`, title: `Reimbursable expenses — ${pick.length} item${pick.length === 1 ? "" : "s"}`,
        ...(p.billing ? taxed(net, p.billing) : { amount: net }), issuedOn, dueOn, status: "due", kind: "expenses",
        items: pick.map((e) => ({ label: `${e.ref} · ${e.description}${e.vendor ? ` — ${e.vendor}` : ""}`, amount: billed(e), date: e.date, note: e.markupPercent ? `${money(e.amount, p.currency)} + ${e.markupPercent}% handling` : undefined })),
      };
      invoiceId = inv.id;
      const ids2 = new Set(pick.map((e) => e.id));
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `Invoice ${inv.ref} issued — reimbursable expenses`, body: `${money(inv.amount, p.currency)}, due ${dueOn}.` };
      return logActivity({
        ...p, invoices: [inv, ...p.invoices],
        expenses: (p.expenses ?? []).map((e) => ids2.has(e.id) ? { ...e, status: "billed" as const, invoiceId: inv.id } : e),
        notices: [notice, ...(p.notices ?? [])].slice(0, 40),
      }, by, `issued ${inv.ref} — ${pick.length} reimbursable expense${pick.length === 1 ? "" : "s"}`, "payment");
    });
    return invoiceId;
  },
  /** The invoice PDF went to the client — keep the file and the moment. */
  markInvoiceSent(pid: string, invoiceId: string, pdf?: { url: string; name: string }) {
    patchProject(pid, (p) => ({ ...p, invoices: p.invoices.map((i) => i.id === invoiceId ? { ...i, sentOn: nowISO(), pdf: pdf ?? i.pdf } : i) }));
  },

  approveBrief(pid: string, note: string) {
    patchProject(pid, (p) => logActivity(
      { ...p, brief: { ...p.brief, status: "approved", approvedBy: p.client.name, approvedOn: realToday(), clientNote: note || p.brief.clientNote } },
      p.client.name, "approved the Design Brief", "brief"));
    const p = state.projects.find((x) => x.id === pid);
    if (p) actions.record(pid, { kind: "brief-approved", title: "Design brief — approved", by: p.client.name });
  },
  requestBriefChanges(pid: string, note: string) {
    patchProject(pid, (p) => logActivity(
      { ...p, brief: { ...p.brief, status: "changes", clientNote: note } },
      p.client.name, "requested changes to the Design Brief", "brief"));
  },
  sendBrief(pid: string) {
    patchProject(pid, (p) => logActivity({ ...p, brief: { ...p.brief, status: "sent" } }, "Studio", "sent the Design Brief for approval", "brief"));
  },
  updateBrief(pid: string, sections: Brief["sections"]) {
    patchProject(pid, (p) => ({ ...p, brief: { ...p.brief, sections, updatedOn: realToday(), status: p.brief.status === "approved" ? "draft" : p.brief.status } }));
  },
  addReference(pid: string, ref: { title: string; url?: string; note?: string }, addedBy: string) {
    patchProject(pid, (p) => logActivity({ ...p, brief: { ...p.brief,
      references: [...(p.brief.references ?? []), { id: uid(), title: ref.title, url: ref.url, note: ref.note, hue: [26, 150, 205, 15, 100][(p.brief.references?.length ?? 0) % 5], addedBy }] } },
      addedBy === "client" ? p.client.name : addedBy, "added a design reference", "brief"));
  },
  removeReference(pid: string, refId: string) {
    patchProject(pid, (p) => ({ ...p, brief: { ...p.brief, references: (p.brief.references ?? []).filter((r) => r.id !== refId) } }));
  },
  setBriefSpaces(pid: string, spaces: Brief["spaces"]) {
    patchProject(pid, (p) => ({ ...p, brief: { ...p.brief, spaces, updatedOn: realToday() } }));
  },
  setAreaUnit(pid: string, unit: "sqft" | "sqm") {
    patchProject(pid, (p) => ({ ...p, brief: { ...p.brief, areaUnit: unit } }));
  },
  suggestSpaces(pid: string) {
    patchProject(pid, (p) => {
      const floors = p.floors ?? 1;
      const spaces = p.types && p.types.length > 1 ? programForTypes(p.types, floors) : programFor(p.types?.[0] || p.type, floors);
      return logActivity({ ...p, brief: { ...p.brief, spaces, updatedOn: realToday() } }, "Studio", "generated a space program", "brief");
    });
  },
  setFloors(pid: string, floors: number) {
    patchProject(pid, (p) => ({ ...p, floors: Math.max(1, Math.round(floors) || 1) }));
  },
  setSurvey(pid: string, v: "yes" | "no" | "unknown") {
    patchProject(pid, (p) => ({ ...p, surveyPlan: v }));
  },
  /** The client supplies a survey/site plan that wasn't on file. It's now
   *  available, so the survey status flips and the studio is notified. */
  provideSurvey(pid: string, doc: { name: string; url?: string; imageId?: string }) {
    patchProject(pid, (p) => {
      const next = { ...p, surveyPlan: "yes" as const, surveyDoc: { ...doc, addedOn: nowISO() } };
      void notify({ to: STUDIO.email, subject: `${p.client.name} provided a survey plan — ${p.name}`, heading: "Survey plan provided", body: `${p.client.name} uploaded a survey / site plan for ${p.name}${doc.name ? `: ${doc.name}` : ""}.`, cta: { label: "Open the project", url: `https://${STUDIO.domain}/studio/${p.id}/data` } });
      return logActivity(next, p.client.name, "provided a survey / site plan", "brief");
    });
  },
  /* ---- billing: payment schedule & invoicing ------------------------------ */
  /** Replace the payment structure. Anything already invoiced is carried over
   *  rather than reset — re-reading a contract used to mint fresh "scheduled"
   *  milestones for work that had already been billed, which is how a ledger
   *  ends up with the same payment on it twice. */
  setPaymentSchedule(pid: string, schedule: PaymentSchedule) {
    patchProject(pid, (p) => {
      const billed = (p.billing?.milestones ?? []).filter((m) => m.status !== "scheduled");
      let milestones = schedule.milestones;
      if (billed.length) {
        const same = (a: PaymentMilestone, b: PaymentMilestone) =>
          a.label.trim().toLowerCase() === b.label.trim().toLowerCase() || (!!a.ref && a.ref === b.ref);
        const merged = schedule.milestones.map((m) => {
          const prior = billed.find((x) => same(x, m));
          return prior ? { ...m, status: prior.status, invoiceId: prior.invoiceId, items: prior.items ?? m.items } : m;
        });
        // anything already billed that the new structure omits stays on record
        const orphaned = billed.filter((x) => !merged.some((m) => same(x, m)));
        milestones = [...merged, ...orphaned];
      }
      const next = { ...schedule, milestones };
      return logActivity({ ...p, billing: next }, STUDIO.principal,
        `set the payment schedule — ${milestones.length} milestone${milestones.length === 1 ? "" : "s"} from the ${schedule.source === "contract" ? "contract" : schedule.source === "stages" ? "fee stages" : "manual entry"}`, "payment");
    });
  },
  updateMilestone(pid: string, mid: string, patch: Partial<PaymentMilestone>) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, milestones: p.billing.milestones.map((m) => m.id === mid ? { ...m, ...patch } : m) } }
      : p);
  },
  addMilestone(pid: string, data: { label: string; pct?: number; amount: number; dueOn?: string; basis?: string }) {
    patchProject(pid, (p) => {
      if (!p.billing) return p;
      const n = p.billing.milestones.length + 1;
      const m: PaymentMilestone = {
        id: uid("pm-"), ref: `PM-${String(n).padStart(2, "0")}`, label: data.label.trim(),
        basis: data.basis?.trim() || undefined, pct: data.pct, amount: data.amount,
        dueOn: data.dueOn || undefined, status: "scheduled",
      };
      return { ...p, billing: { ...p.billing, milestones: [...p.billing.milestones, m] } };
    });
  },
  removeMilestone(pid: string, mid: string) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, milestones: p.billing.milestones.filter((m) => m.id !== mid) } }
      : p);
  },
  /** Raise the invoice for a milestone and send it — the schedule keeps the link. */
  /** Turn VAT/tax on or off for this project's billing, and set its rate/label.
   *  Only affects invoices raised AFTER the change — issued invoices keep the
   *  rate captured on them. */
  /** Remove the deferred-payment clause — for contracts that have none, or when
   *  a scan detected one in error. Money already deferred on valuations is not
   *  touched; this only clears the schedule-level projection. */
  clearDeferred(pid: string) {
    patchProject(pid, (p) => p.billing
      ? logActivity({ ...p, billing: { ...p.billing, deferred: undefined } }, STUDIO.principal, "removed the deferred-payment clause", "payment")
      : p);
  },
  setBillingTax(pid: string, tax: { enabled: boolean; label?: string; rate?: number }) {
    patchProject(pid, (p) => p.billing ? {
      ...p,
      billing: { ...p.billing, tax: { enabled: tax.enabled, label: tax.label?.trim() || p.billing.tax?.label || "VAT", rate: tax.rate ?? p.billing.tax?.rate ?? 0 } },
    } : p);
  },
  issueInvoiceForMilestone(pid: string, mid: string) {
    patchProject(pid, (p) => {
      const b = p.billing;
      const m = b?.milestones.find((x) => x.id === mid);
      if (!b || !m || m.status !== "scheduled") return p;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (b.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const inv: Invoice = {
        id: uid("inv-"), ref: `INV-${tail}-${seq}`, title: m.label,
        ...taxed(m.amount, b), issuedOn, dueOn, status: "due",
      };
      void notify({
        to: p.client.email, subject: `Invoice ${inv.ref} — ${p.name}`,
        heading: `Invoice ${inv.ref}`,
        body: `${m.label} — ${money(m.amount, b.currency)}, due ${dueOn}.${m.basis ? `\n\nBasis: ${m.basis}` : ""}\n\nYou can see it against the payment schedule in your portal.`,
        cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` },
      });
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `Invoice ${inv.ref} issued`, body: `${m.label} — ${money(m.amount, b.currency)}, due ${dueOn}.` };
      return logActivity({
        ...p,
        invoices: [inv, ...p.invoices],
        billing: { ...b, milestones: b.milestones.map((x) => x.id === mid ? { ...x, status: "invoiced" as const, invoiceId: inv.id } : x) },
        notices: [notice, ...(p.notices ?? [])].slice(0, 40),
      }, STUDIO.principal, `issued ${inv.ref} — ${m.label}`, "payment");
    });
  },
  /** Turn the advance retainer on/off and set its rate (default 15% of the fee).
   *  The amount is always re-derived from the current contract sum. */
  setRetainer(pid: string, on: boolean, pct?: number) {
    patchProject(pid, (p) => {
      const b = p.billing;
      if (!b) return p;
      if (!on) return { ...p, billing: { ...b, retainer: undefined } };
      const usePct = pct ?? b.retainer?.pct ?? 15;
      const amount = Math.round(b.contractSum * (usePct / 100));
      return { ...p, billing: { ...b, retainer: { pct: usePct, amount, note: b.retainer?.note, invoiceId: b.retainer?.invoiceId, creditPerStage: b.retainer?.creditPerStage ?? true } } };
    });
  },
  /** Invoice the advance retainer up-front, before the stage milestones run. */
  issueRetainerInvoice(pid: string) {
    patchProject(pid, (p) => {
      const b = p.billing; const r = b?.retainer;
      if (!b || !r || r.invoiceId) return p;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (b.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const title = `Advance retainer (${r.pct}% of fee)`;
      const inv: Invoice = { id: uid("inv-"), ref: `INV-${tail}-${seq}`, title, ...taxed(r.amount, b), issuedOn, dueOn, status: "due" };
      void notify({
        to: p.client.email, subject: `Invoice ${inv.ref} — ${p.name}`,
        heading: `Advance retainer ${inv.ref}`,
        body: `An advance retainer of ${money(r.amount, b.currency)} (${r.pct}% of the design fee), due ${dueOn}. This secures the appointment and is credited back as each design stage is billed.`,
        cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` },
      });
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `Advance retainer ${inv.ref} issued`, body: `${money(r.amount, b.currency)} (${r.pct}% of the fee), due ${dueOn}.` };
      return logActivity({
        ...p,
        invoices: [inv, ...p.invoices],
        billing: { ...b, retainer: { ...r, invoiceId: inv.id } },
        notices: [notice, ...(p.notices ?? [])].slice(0, 40),
      }, STUDIO.principal, `issued ${inv.ref} — advance retainer (${r.pct}%)`, "payment");
    });
  },
  /** What a milestone covers, itemised — this is what the client sees. */
  setMilestoneItems(pid: string, mid: string, items: { id?: string; label: string; amount?: number; note?: string }[]) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, milestones: p.billing.milestones.map((m) => m.id === mid
          ? { ...m, items: items.filter((i) => i.label.trim()).map((i) => ({ id: i.id ?? uid("mi-"), label: i.label.trim(), amount: i.amount, note: i.note?.trim() || undefined })) }
          : m) } }
      : p);
  },
  /** Fill the breakdown from the drawings that stage actually delivers. */
  suggestMilestoneItems(pid: string, mid: string) {
    patchProject(pid, (p) => {
      const b = p.billing;
      const m = b?.milestones.find((x) => x.id === mid);
      if (!b || !m) return p;
      const phase = /schematic|concept/i.test(m.label) ? "Conceptual"
        : /developed|design development/i.test(m.label) ? "Design Development"
        : /construction doc|technical|ift|tender/i.test(m.label) ? "IFT" : null;
      const sheets = phase ? (p.deliverables ?? []).filter((d) => d.phase === phase) : [];
      if (!sheets.length) return p;
      const items = sheets.slice(0, 24).map((d) => ({ id: uid("mi-"), label: `${d.code} — ${d.title}`, note: d.discipline }));
      return { ...p, billing: { ...b, milestones: b.milestones.map((x) => x.id === mid ? { ...x, items } : x) } };
    });
  },
  /** Record money received against an invoice. Part payments are normal — the
   *  invoice only settles once the receipts cover it, and the milestone follows. */
  recordInvoicePayment(pid: string, invoiceId: string, data: { amount: number; on?: string; method?: string; reference?: string; note?: string }) {
    patchProject(pid, (p) => {
      const inv = p.invoices.find((i) => i.id === invoiceId);
      if (!inv || data.amount <= 0) return p;
      const pay: InvoicePayment = {
        id: uid("pay-"), amount: data.amount, on: data.on || realToday(),
        method: data.method?.trim() || undefined, reference: data.reference?.trim() || undefined, note: data.note?.trim() || undefined,
        status: "confirmed", by: STUDIO.principal,
      };
      const payments = [...(inv.payments ?? []), pay];
      const received = payments.filter(isConfirmed).reduce((s, x) => s + x.amount, 0);
      const settled = received >= inv.amount - 0.5;
      const invoices = p.invoices.map((i) => i.id === invoiceId
        ? { ...i, payments, status: settled ? ("paid" as const) : i.status, paidOn: settled ? pay.on : i.paidOn }
        : i);
      const billing = p.billing
        ? { ...p.billing, milestones: p.billing.milestones.map((m) => m.invoiceId === invoiceId && settled ? { ...m, status: "paid" as const } : m) }
        : p.billing;
      // the receipts behind an expenses invoice are reimbursed the moment it settles
      const expenses = settled ? (p.expenses ?? []).map((e) => e.invoiceId === invoiceId ? { ...e, status: "reimbursed" as const, reimbursedOn: pay.on } : e) : p.expenses;
      // the receipt goes to both parties as a document of record
      setTimeout(() => actions.record(pid, { kind: "payment-received", refId: pay.id, title: `${inv.ref} — ${money(pay.amount, p.currency)} received${settled ? " · settled" : ""}`, by: STUDIO.principal }), 0);
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `Payment received — ${money(pay.amount, p.currency)}`, body: `Thank you. ${settled ? `${inv.ref} is now settled.` : `${money(inv.amount - received, p.currency)} remains on ${inv.ref}.`}` };
      return logActivity({ ...p, invoices, billing, expenses, notices: [notice, ...(p.notices ?? [])].slice(0, 40) },
        STUDIO.principal, `recorded ${money(pay.amount, p.currency)} against ${inv.ref}${settled ? " — settled" : ""}`, "payment");
    });
  },
  /** The client telling us they have paid, with their evidence attached. This
   *  settles nothing: it is a claim sitting on the invoice until the studio
   *  checks the account and confirms it. */
  claimInvoicePayment(pid: string, invoiceId: string, data: { amount: number; on?: string; method?: string; reference?: string; note?: string; proof?: { url: string; name: string }; by: string }) {
    patchProject(pid, (p) => {
      const inv = p.invoices.find((i) => i.id === invoiceId);
      if (!inv || data.amount <= 0) return p;
      const pay: InvoicePayment = {
        id: uid("pay-"), amount: data.amount, on: data.on || realToday(),
        method: data.method?.trim() || undefined, reference: data.reference?.trim() || undefined, note: data.note?.trim() || undefined,
        status: "claimed", by: data.by, proof: data.proof,
      };
      return logActivity(
        { ...p, invoices: p.invoices.map((i) => i.id === invoiceId ? { ...i, payments: [...(i.payments ?? []), pay] } : i) },
        data.by, `submitted proof of payment — ${money(pay.amount, p.currency)} against ${inv.ref}`, "payment");
    });
  },
  /** The studio checking the account and accepting a claim. Only here does the
   *  money become real, and only here can an invoice settle from a claim. */
  confirmInvoicePayment(pid: string, invoiceId: string, payId: string, by: string) {
    patchProject(pid, (p) => {
      const inv = p.invoices.find((i) => i.id === invoiceId);
      const pay = inv?.payments?.find((x) => x.id === payId);
      if (!inv || !pay) return p;
      const payments = (inv.payments ?? []).map((x) => x.id === payId ? { ...x, status: "confirmed" as const } : x);
      const received = payments.filter(isConfirmed).reduce((s, x) => s + x.amount, 0);
      const settled = received >= inv.amount - 0.5;
      const invoices = p.invoices.map((i) => i.id === invoiceId
        ? { ...i, payments, status: settled ? ("paid" as const) : i.status, paidOn: settled ? pay.on : i.paidOn }
        : i);
      const billing = p.billing
        ? { ...p.billing, milestones: p.billing.milestones.map((m) => m.invoiceId === invoiceId && settled ? { ...m, status: "paid" as const } : m) }
        : p.billing;
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `Payment confirmed — ${money(pay.amount, p.currency)}`, body: `Thank you. ${settled ? `${inv.ref} is now settled.` : `${money(inv.amount - received, p.currency)} remains on ${inv.ref}.`}` };
      return logActivity({ ...p, invoices, billing, notices: [notice, ...(p.notices ?? [])].slice(0, 40) },
        by, `confirmed ${money(pay.amount, p.currency)} against ${inv.ref}${settled ? " — settled" : ""}`, "payment");
    });
  },
  /** Reject a claim that never arrived — it leaves the ledger entirely. */
  rejectInvoicePayment(pid: string, invoiceId: string, payId: string, by: string) {
    patchProject(pid, (p) => {
      const inv = p.invoices.find((i) => i.id === invoiceId);
      const pay = inv?.payments?.find((x) => x.id === payId);
      if (!inv || !pay || isConfirmed(pay)) return p;      // confirmed money is never silently removed
      return logActivity(
        { ...p, invoices: p.invoices.map((i) => i.id === invoiceId ? { ...i, payments: (i.payments ?? []).filter((x) => x.id !== payId) } : i) },
        by, `could not trace ${money(pay.amount, p.currency)} claimed against ${inv.ref}`, "payment");
    });
  },
  /* ---- stage valuations: certify by % complete, per discipline ---- */
  setValuation(pid: string, v: StageValuation) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, valuations: [...(p.billing.valuations ?? []).filter((x) => x.id !== v.id), v] } }
      : p);
  },
  /** Move a scope line's progress — the earned, deferred and net all follow. */
  setValuationProgress(pid: string, vid: string, lineId: string, pctComplete: number) {
    const pc = Math.max(0, Math.min(1, pctComplete));
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, valuations: (p.billing.valuations ?? []).map((v) => v.id === vid
          ? { ...v, lines: v.lines.map((l) => l.id === lineId ? { ...l, pctComplete: pc } : l) } : v) } }
      : p);
  },
  setValuationLineValue(pid: string, vid: string, lineId: string, value: number) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, valuations: (p.billing.valuations ?? []).map((v) => v.id === vid
          ? { ...v, lines: v.lines.map((l) => l.id === lineId ? { ...l, value: Math.max(0, value) } : l) } : v) } }
      : p);
  },
  /** Raise the invoice for a valuation's NET total — the deferred part is held
   *  back and only falls due on the engagement's deferral trigger. */
  certifyValuation(pid: string, vid: string, net: number, deferred: number) {
    patchProject(pid, (p) => {
      const b = p.billing;
      const v = b?.valuations?.find((x) => x.id === vid);
      if (!b || !v || net <= 0) return p;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (b.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const inv: Invoice = {
        id: uid("inv-"), ref: `INV-${tail}-${seq}`, title: `${v.stage} — valuation`,
        ...taxed(net, b), issuedOn, dueOn, status: "due",
      };
      void notify({
        to: p.client.email, subject: `Invoice ${inv.ref} — ${p.name}`,
        heading: `${v.stage} valuation`,
        body: `${v.stage} certified — ${money(net, b.currency)} payable, due ${dueOn}.` +
          (deferred > 0 ? `\n\n${money(deferred, b.currency)} is held as the deferred portion and falls due on the agreed trigger, not now.` : ""),
        cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` },
      });
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "payment", title: `${v.stage} valuation — ${money(net, b.currency)}`, body: deferred > 0 ? `${money(deferred, b.currency)} deferred as agreed.` : undefined };
      return logActivity({
        ...p,
        invoices: [inv, ...p.invoices],
        billing: { ...b, valuations: (b.valuations ?? []).map((x) => x.id === vid ? { ...x, certifiedOn: nowISO(), invoiceId: inv.id } : x) },
        notices: [notice, ...(p.notices ?? [])].slice(0, 40),
      }, STUDIO.principal, `certified the ${v.stage} valuation — ${money(net, b.currency)}`, "payment");
    });
  },

  /** Bill one month of a stage drawn monthly — the remaining balance spread
   *  over the months still to run, net of the deferred portion. */
  billMonthlyDraw(pid: string, vid: string, gross: number, net: number, deferred: number) {
    patchProject(pid, (p) => {
      const b = p.billing;
      const v = b?.valuations?.find((x) => x.id === vid);
      if (!b || !v?.monthly || net <= 0) return p;
      const monthNo = v.monthly.drawn + 1;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (b.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const inv: Invoice = {
        id: uid("inv-"), ref: `INV-${tail}-${seq}`,
        title: `${v.stage} — month ${monthNo} of ${v.monthly.months}`,
        ...taxed(net, b), issuedOn, dueOn, status: "due",
      };
      void notify({
        to: p.client.email, subject: `Invoice ${inv.ref} — ${p.name}`,
        heading: `${v.stage} · month ${monthNo}`,
        body: `Monthly draw ${monthNo} of ${v.monthly.months} — ${money(net, b.currency)} payable, due ${dueOn}.` +
          (deferred > 0 ? `\n\n${money(deferred, b.currency)} of this month's ${money(gross, b.currency)} is held as the deferred portion.` : ""),
        cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` },
      });
      return logActivity({
        ...p,
        invoices: [inv, ...p.invoices],
        billing: { ...b, valuations: (b.valuations ?? []).map((x) => x.id === vid
          ? { ...x, monthly: { ...x.monthly!, drawn: monthNo, drawnAmount: x.monthly!.drawnAmount + gross } } : x) },
        notices: [{ id: uid(), on: nowISO(), kind: "payment" as const, title: `${v.stage} month ${monthNo} — ${money(net, b.currency)}` }, ...(p.notices ?? [])].slice(0, 40),
      }, STUDIO.principal, `billed ${v.stage} month ${monthNo} — ${money(net, b.currency)}`, "payment");
    });
  },

  /** Show the client the schedule they'll be billed against. */
  issueSchedule(pid: string) {
    patchProject(pid, (p) => {
      const b = p.billing;
      if (!b) return p;
      void notify({
        to: p.client.email, subject: `Payment schedule for ${p.name}`,
        heading: "Your payment schedule",
        body: `The payment schedule for ${p.name} is now in your portal — ${b.milestones.length} milestone${b.milestones.length === 1 ? "" : "s"} totalling ${money(b.contractSum, b.currency)}. Invoices are raised against it as each point is reached.`,
        cta: { label: "See the schedule", url: `https://${STUDIO.domain}/portal/${p.id}/billing` },
      });
      return logActivity({ ...p, billing: { ...b, issuedOn: nowISO() } }, STUDIO.principal, "issued the payment schedule to the client", "payment");
    });
  },

  /* ---- concept directions ------------------------------------------------ */
  addConcept(pid: string, data: { title: string; note?: string; tags?: string[]; url?: string }) {
    patchProject(pid, (p) => {
      const hues = [26, 205, 150, 15, 190, 100];
      const c: ConceptBoard = {
        id: uid("cb-"), title: data.title.trim(), note: data.note?.trim() || "",
        tags: (data.tags ?? []).filter(Boolean), hue: hues[p.concepts.length % hues.length],
        url: data.url, createdOn: nowISO(),
      };
      return logActivity({ ...p, concepts: [...p.concepts, c] }, STUDIO.principal, `added the concept direction "${c.title}"`, "design");
    });
  },
  updateConcept(pid: string, cid: string, patch: Partial<Pick<ConceptBoard, "title" | "note" | "tags" | "url">>) {
    patchProject(pid, (p) => ({ ...p, concepts: p.concepts.map((c) => c.id === cid ? { ...c, ...patch } : c) }));
  },
  removeConcept(pid: string, cid: string) {
    patchProject(pid, (p) => ({ ...p, concepts: p.concepts.filter((c) => c.id !== cid) }));
  },
  /** Issue the directions to the client — they appear in the portal and the
   *  client is told there's something to look through. */
  presentConcepts(pid: string) {
    patchProject(pid, (p) => {
      if (!p.concepts.length) return p;
      void notify({
        to: p.client.email, subject: `Concept directions for ${p.name}`,
        heading: "Your concept directions are ready",
        body: `We've put ${p.concepts.length} direction${p.concepts.length === 1 ? "" : "s"} together for ${p.name}. Open your portal to look through them and tell us which one you're drawn to.`,
        cta: { label: "See the directions", url: `https://${STUDIO.domain}/portal/${p.id}/direction` },
      });
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: "Concept directions ready", body: `${p.concepts.length} direction${p.concepts.length === 1 ? "" : "s"} to look through.` };
      return logActivity({ ...p, conceptsPresentedOn: nowISO(), notices: [notice, ...(p.notices ?? [])].slice(0, 40) },
        STUDIO.principal, "presented the concept directions to the client", "design");
    });
  },
  /** The client says which direction they're drawn to (one preferred at a time). */
  respondToConcept(pid: string, cid: string, choice: "preferred" | "no", comment: string, by: string) {
    patchProject(pid, (p) => {
      const c = p.concepts.find((x) => x.id === cid);
      void notify({
        to: STUDIO.email, subject: `${by} responded to a concept direction — ${p.name}`,
        heading: choice === "preferred" ? "The client picked a direction" : "The client ruled a direction out",
        body: `${by} marked "${c?.title ?? "a direction"}" as ${choice === "preferred" ? "the one they're drawn to" : "not for them"} on ${p.name}.${comment.trim() ? `\n\nTheir note: ${comment.trim()}` : ""}`,
        cta: { label: "Open the project", url: `https://${STUDIO.domain}/studio/${p.id}/concept` },
      });
      return logActivity({
        ...p,
        concepts: p.concepts.map((x) => x.id === cid
          ? { ...x, response: { choice, comment: comment.trim() || undefined, on: nowISO(), by } }
          : choice === "preferred" && x.response?.choice === "preferred" ? { ...x, response: undefined } : x),
      }, by, choice === "preferred" ? `chose the "${c?.title}" direction` : `ruled out "${c?.title}"`, "design");
    });
  },

  /* ---- the client appoints specialist sub-consultants (structural / MEP) ---- */
  appointConsultant(pid: string, input: { discipline: ConsultantDiscipline; firm: string; contact?: string; email?: string; phone?: string; note?: string }) {
    patchProject(pid, (p) => {
      const c: Consultant = {
        id: uid(), discipline: input.discipline, firm: input.firm.trim(),
        contact: input.contact?.trim() || undefined, email: input.email?.trim() || undefined,
        phone: input.phone?.trim() || undefined, note: input.note?.trim() || undefined,
        status: "appointed", appointedBy: "client", appointedOn: nowISO(),
      };
      // one appointment per discipline — a fresh one replaces the last
      const consultants = [...(p.consultants ?? []).filter((x) => x.discipline !== input.discipline), c];
      void notify({
        to: STUDIO.email,
        subject: `${p.client.name} appointed a ${input.discipline} consultant — ${p.name}`,
        heading: `${input.discipline} sub-consultant appointed`,
        body: `${p.client.name} has appointed ${c.firm}${c.contact ? ` (${c.contact})` : ""} as the ${input.discipline} sub-consultant for ${p.name}.`
          + (c.email ? ` Email: ${c.email}.` : "") + (c.phone ? ` Phone: ${c.phone}.` : ""),
        cta: { label: "Open the project", url: `https://${STUDIO.domain}/studio/${p.id}/dashboard` },
      });
      return logActivity({ ...p, consultants }, p.client.name, `appointed ${c.firm} for ${input.discipline}`, "stage");
    });
  },
  removeConsultant(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, consultants: (p.consultants ?? []).filter((c) => c.id !== id) }));
  },
  setProjectType(pid: string, type: string) {
    patchProject(pid, (p) => ({ ...p, type, types: [type] }));
  },
  setProjectTypes(pid: string, types: string[]) {
    patchProject(pid, (p) => ({ ...p, types, type: types.length ? types.join(" · ") : p.type }));
  },
  setGia(pid: string, gia: number, unit: "sqft" | "sqm") {
    patchProject(pid, (p) => ({ ...p, gia, giaUnit: unit, area: gia ? `${gia.toLocaleString()} ${unit === "sqm" ? "m²" : "ft²"}` : p.area }));
  },
  updateProject(pid: string, patch: Partial<Pick<Project, "name" | "type" | "location" | "area" | "budget" | "currency" | "surveyPlan" | "geo">>) {
    patchProject(pid, (p) => ({ ...p, ...patch }));
  },
  updateClient(pid: string, patch: Partial<Pick<Client, "name" | "company" | "email" | "phone">>) {
    patchProject(pid, (p) => ({ ...p, client: { ...p.client, ...patch } }));
  },
  /* Additional people on the client side. The one marked primary is mirrored
     into the Client's own name/email/phone so everything that reads the "main
     contact" (invites, portal, WhatsApp) stays in step. */
  addClientContact(pid: string, c: { name: string; role?: string; email?: string; phone?: string; primary?: boolean }) {
    patchProject(pid, (p) => {
      const contact: ClientContact = { id: uid("cc-"), name: c.name.trim(), role: c.role?.trim() || undefined, email: c.email?.trim() || undefined, phone: c.phone?.trim() || undefined, primary: c.primary };
      let contacts = [...(p.client.contacts ?? []), contact];
      if (contact.primary) contacts = contacts.map((x) => x.id === contact.id ? x : { ...x, primary: false });
      const client = contact.primary
        ? { ...p.client, contacts, name: contact.name, email: contact.email ?? p.client.email, phone: contact.phone ?? p.client.phone }
        : { ...p.client, contacts };
      return logActivity({ ...p, client }, "Studio", `added client contact ${contact.name}${contact.role ? ` (${contact.role})` : ""}`, "brief");
    });
  },
  updateClientContact(pid: string, id: string, patch: Partial<ClientContact>) {
    patchProject(pid, (p) => {
      const existing = (p.client.contacts ?? []).find((x) => x.id === id);
      if (!existing) return p;
      let contacts = (p.client.contacts ?? []).map((x) => x.id === id ? { ...x, ...patch } : x);
      const next = contacts.find((x) => x.id === id)!;
      // making one primary demotes the others, and re-syncs the main contact
      if (patch.primary) contacts = contacts.map((x) => x.id === id ? x : { ...x, primary: false });
      const client = next.primary
        ? { ...p.client, contacts, name: next.name, email: next.email ?? p.client.email, phone: next.phone ?? p.client.phone }
        : { ...p.client, contacts };
      return { ...p, client };
    });
  },
  removeClientContact(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, client: { ...p.client, contacts: (p.client.contacts ?? []).filter((x) => x.id !== id) } }));
  },
  reissueClientCode(pid: string) {
    patchProject(pid, (p) => logActivity({ ...p, client: { ...p.client, code: makeCode(p.client.company || p.client.name) } }, "Studio", "reissued the client access code", "brief"));
  },
  issuePresentation(pid: string) {
    patchProject(pid, (p) => logActivity({ ...p, presentation: { ...p.presentation, status: "sent", issuedOn: realToday() } }, "Studio", "issued the design presentation for review", "design"));
  },
  respondToSpace(pid: string, spaceId: string, response: DesignSpace["response"], comment: string) {
    patchProject(pid, (p) => {
      const spaces = p.presentation.spaces.map((s) => s.id === spaceId ? { ...s, response, comment: comment || s.comment, respondedOn: realToday() } : s);
      const sp = spaces.find((s) => s.id === spaceId)!;
      const verb = response === "approved" ? "approved" : "requested a revision on";
      let np: Project = { ...p, presentation: { ...p.presentation, spaces } };
      if (comment && response === "revise") {
        const fb: FeedbackItem = { id: uid(), author: "client", authorName: p.client.name, context: `${sp.name} — ${sp.option}`, body: comment, createdOn: nowISO(), resolved: false, replies: [] };
        np = { ...np, feedback: [fb, ...np.feedback] };
      }
      return logActivity(np, p.client.name, `${verb} the ${sp.name} design`, "design");
    });
    const p = state.projects.find((x) => x.id === pid);
    if (p) void notify({ to: STUDIO.email, subject: `${p.client.name}: ${response === "approved" ? "approved" : "revision on"} a design — ${p.name}`,
      heading: response === "approved" ? "Design approved" : "Revision requested",
      body: `${p.client.name} responded on ${p.name}.${comment ? `\n\n“${comment}”` : ""}`,
      cta: { label: "Open the presentation", url: `https://${STUDIO.domain}/studio/${p.id}/presentation` } });
  },
  /* Settling in one click still has to leave a receipt behind, or the payment
     shows in the totals with nothing in the ledger to trace it to. Studio-side
     only: a client cannot mark their own invoice paid — they submit proof and
     the studio confirms it against the account. */
  payInvoice(pid: string, invId: string) {
    const p = state.projects.find((x) => x.id === pid);
    const inv = p?.invoices.find((i) => i.id === invId);
    if (!inv) return;
    const balance = Math.max(0, inv.amount - confirmedPaid(inv));
    if (balance <= 0) return;
    actions.recordInvoicePayment(pid, invId, { amount: balance, method: "Recorded by studio" });
  },
  advanceStage(pid: string) {
    patchProject(pid, (p) => {
      const idx = p.stages.findIndex((s) => s.status === "active");
      if (idx < 0 || idx >= p.stages.length - 1) return p;
      const stages = p.stages.map((s, i) => i === idx ? { ...s, status: "done" as const, completedOn: realToday() }
        : i === idx + 1 ? { ...s, status: "active" as const, startedOn: realToday() } : s);
      return logActivity({ ...p, stages }, "Studio", `advanced the project to ${p.stages[idx + 1].label}`, "stage");
    });
  },
  prepareReport(pid: string, title: string, period: string, summary: string, highlights: string[]) {
    patchProject(pid, (p) => logActivity(
      { ...p, reports: [{ id: uid(), title, period, issuedOn: "", status: "draft", summary, highlights: highlights.filter(Boolean) }, ...p.reports] },
      "Studio", "drafted a new progress report", "report"));
  },
  issueReport(pid: string, rid: string) {
    patchProject(pid, (p) => logActivity(
      { ...p, reports: p.reports.map((r) => r.id === rid ? { ...r, status: "issued" as const, issuedOn: realToday() } : r) },
      "Studio", "issued a progress report", "report"));
  },
  resolveFeedback(pid: string, fid: string) {
    patchProject(pid, (p) => ({ ...p, feedback: p.feedback.map((f) => f.id === fid ? { ...f, resolved: !f.resolved } : f) }));
  },
  replyFeedback(pid: string, fid: string, author: "client" | "studio", name: string, body: string) {
    patchProject(pid, (p) => ({ ...p, feedback: p.feedback.map((f) => f.id === fid
      ? { ...f, replies: [...f.replies, { id: uid(), author, authorName: name, body, createdOn: nowISO() }] } : f) }));
  },
  addFeedback(pid: string, author: "client" | "studio", name: string, context: string, body: string) {
    patchProject(pid, (p) => logActivity(
      { ...p, feedback: [{ id: uid(), author, authorName: name, context, body, createdOn: nowISO(), resolved: false, replies: [] }, ...p.feedback] },
      name, "left a comment", "feedback"));
  },

  /* ---- projects ---- */
  /** Remove a project outright — used by the admin to clear the seeded template
   *  projects. Irreversible; the caller confirms first. */
  deleteProject(pid: string) {
    commit({ ...state, projects: state.projects.filter((p) => p.id !== pid) });
  },
  createProject(input: {
    name: string; type: string; location: string; area: string; budget: number; currency: string;
    clientName: string; clientCompany: string; clientEmail: string;
    briefSections?: { title: string; body: string }[];
    surveyPlan?: "yes" | "no" | "unknown"; geo?: { lat: number; lng: number };
    /** Onboard a project already under way: the engagement is signed, the brief
     *  approved, and the stages advanced to where the work actually stands. */
    existing?: boolean; currentStage?: StageKey;
    /** Seed the register, fee model and brief from a saved template. */
    templateId?: string;
  }): string {
    const tpl = input.templateId ? (state.templates ?? []).find((t) => t.id === input.templateId) : undefined;
    const n = state.projects.length + 9;
    const id = uid("p-");
    const hue = [26, 205, 150, 15, 190, 100][state.projects.length % 6];
    // An existing (already-signed) project starts past engagement + brief, at the
    // stage the studio says it's reached (concept at the earliest).
    const signed = !!input.existing;
    const stageIdx = signed ? Math.max(2, STAGE_ORDER.findIndex((s) => s.key === (input.currentStage ?? "concept"))) : 0;
    const today = realToday();
    // kick-off a week out from creation, not a frozen calendar date
    const kickoff = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
    const client: Client = {
      id: uid("c-"), name: input.clientName, company: input.clientCompany, email: input.clientEmail,
      avatarHue: hue, since: today, code: makeCode(input.clientCompany || input.clientName),
    };
    const p: Project = {
      id, name: input.name, code: `ATL·2026·${String(n).padStart(3, "0")}`, client,
      type: input.type, types: [input.type], location: input.location, area: input.area, budget: input.budget, currency: input.currency,
      cover: hue, createdOn: today, stages: buildStages(stageIdx),
      surveyPlan: input.surveyPlan ?? "unknown", geo: input.geo,
      brief: { updatedOn: today, status: signed ? "approved" : "draft",
        approvedBy: signed ? input.clientName : undefined, approvedOn: signed ? today : undefined,
        references: [], areaUnit: "sqft", spaces: programFor(input.type), sections: ((input.briefSections ?? tpl?.briefSections)?.length
        ? (input.briefSections ?? tpl!.briefSections!).map((s) => ({ id: uid(), title: s.title, body: s.body }))
        : BRIEF_TEMPLATE.map((s) => ({ id: uid(), title: s.title, body: "", hint: s.hint }))) },
      engagement: signed ? { ...defaultEngagement(), status: "signed", issuedOn: today, signedOn: today, signedBy: input.clientName } : undefined,
      concepts: [],
      presentation: { id: uid("pr-"), title: "Developed Design — Client Review 01", issuedOn: "", status: "draft", spaces: [] },
      schedule: [{ id: uid(), title: "Kick-off & brief workshop", date: kickoff, kind: "meeting", stage: "brief" }],
      program: [], deliverables: tpl?.deliverables?.length
        ? tpl.deliverables.map((d) => ({ id: uid("dl-"), code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, building: d.building, status: "todo" as const, kind: "drawing" as const }))
        : iftPackageFor(input.type),
      estimate: tpl?.estimate,
      reports: [], quotations: [], invoices: [], feedback: [],
      activity: [{ id: uid(), on: nowISO(), who: STUDIO.principal, what: `opened ${input.name}${tpl ? ` from template “${tpl.name}”` : ""}`, kind: "stage" }],
    };
    commit({ ...state, projects: [p, ...state.projects] });
    return id;
  },

  /* ---- project templates: save a skeleton, reuse it on repeat typologies ---- */
  saveTemplate(pid: string, name: string, by: string, note?: string): string {
    const p = state.projects.find((x) => x.id === pid);
    const id = uid("tpl-");
    if (!p) return id;
    const tpl: import("./types").ProjectTemplate = {
      id, name: name.trim() || `${p.name} template`, note: note?.trim() || undefined,
      createdOn: realToday(), createdBy: by, type: p.type, types: p.types,
      deliverables: (p.deliverables ?? []).map((d) => ({ code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, building: d.building })),
      milestones: (p.billing?.milestones ?? []).map((m) => ({ label: m.label, basis: m.basis, pct: m.pct })),
      estimate: p.estimate,
      briefSections: (p.brief?.sections ?? []).map((s) => ({ title: s.title, body: s.body })),
    };
    commit({ ...state, templates: [tpl, ...(state.templates ?? [])] });
    return id;
  },
  removeTemplate(id: string) { commit({ ...state, templates: (state.templates ?? []).filter((t) => t.id !== id) }); },

  /* ---- leads: the pipeline before a project exists ---- */
  createLead(data: { name: string; clientName: string; company?: string; email?: string; phone?: string; type?: string; location?: string; budget?: number; currency?: string; fee?: number; source?: string; note?: string }): string {
    const id = uid("ld-");
    const n = (state.leads ?? []).length ? Math.max(...(state.leads ?? []).map((l) => parseInt(l.ref.split("-")[1], 10) || 0)) + 1 : 1;
    const lead: import("./types").Lead = {
      id, ref: `LEAD-${String(n).padStart(3, "0")}`, name: data.name.trim(), clientName: data.clientName.trim(),
      company: data.company?.trim() || undefined, email: data.email?.trim() || undefined, phone: data.phone?.trim() || undefined,
      type: data.type, location: data.location?.trim() || undefined, budget: data.budget, currency: data.currency || "USD",
      fee: data.fee, status: "enquiry", source: data.source?.trim() || undefined, note: data.note?.trim() || undefined,
      createdOn: realToday(),
    };
    commit({ ...state, leads: [lead, ...(state.leads ?? [])] });
    return id;
  },
  updateLead(id: string, patch: Partial<import("./types").Lead>) {
    commit({ ...state, leads: (state.leads ?? []).map((l) => l.id === id ? { ...l, ...patch } : l) });
  },
  setLeadStatus(id: string, status: import("./types").LeadStatus, lostReason?: string) {
    commit({ ...state, leads: (state.leads ?? []).map((l) => l.id === id ? {
      ...l, status,
      proposalSentOn: status === "proposal" ? (l.proposalSentOn ?? realToday()) : l.proposalSentOn,
      decidedOn: status === "won" || status === "lost" ? realToday() : undefined,
      lostReason: status === "lost" ? (lostReason?.trim() || l.lostReason) : undefined,
    } : l) });
  },
  removeLead(id: string) { commit({ ...state, leads: (state.leads ?? []).filter((l) => l.id !== id) }); },

  /* ---- snags / punch list ---- */
  addSnag(pid: string, data: { title: string; note?: string; location?: string; trade?: string; severity?: "high" | "medium" | "low"; photos: string[]; by: string }) {
    patchProject(pid, (p) => {
      const n = (p.snags ?? []).length ? Math.max(...(p.snags ?? []).map((s) => parseInt(s.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const s: import("./types").Snag = {
        id: uid("sn-"), ref: `SNG-${String(n).padStart(3, "0")}`, title: data.title.trim(), note: data.note?.trim() || undefined,
        location: data.location?.trim() || undefined, trade: data.trade?.trim() || undefined, severity: data.severity,
        status: "open", photos: data.photos, raisedBy: data.by, raisedOn: nowISO(),
      };
      return logActivity({ ...p, snags: [s, ...(p.snags ?? [])] }, data.by, `raised ${s.ref} — ${s.title}`, "design");
    });
  },
  setSnagStatus(pid: string, sid: string, status: import("./types").SnagStatus, by: string) {
    patchProject(pid, (p) => ({
      ...p, snags: (p.snags ?? []).map((s) => s.id === sid ? {
        ...s, status,
        fixedOn: status === "fixed" ? nowISO() : s.fixedOn,
        acceptedBy: status === "accepted" ? by : undefined, acceptedOn: status === "accepted" ? nowISO() : undefined,
      } : s),
    }));
  },
  removeSnag(pid: string, sid: string) { patchProject(pid, (p) => ({ ...p, snags: (p.snags ?? []).filter((s) => s.id !== sid) })); },

  /* ---- formal stage approvals the client e-signs ---- */
  issueApproval(pid: string, data: { title: string; stage?: string; note?: string; sheets: { code: string; title: string; revision?: string }[]; by: string }): string {
    const id = uid("app-");
    patchProject(pid, (p) => {
      const n = (p.approvals ?? []).length ? Math.max(...(p.approvals ?? []).map((a) => parseInt(a.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const a: import("./types").Approval = {
        id, ref: `APP-${String(n).padStart(3, "0")}`, title: data.title.trim(), stage: data.stage,
        note: data.note?.trim() || undefined, sheets: data.sheets, status: "issued",
        issuedOn: nowISO(), issuedBy: data.by,
      };
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `Approval requested — ${a.title}`, body: `${a.sheets.length} sheet${a.sheets.length === 1 ? "" : "s"} for your sign-off.` };
      return logActivity({ ...p, approvals: [a, ...(p.approvals ?? [])], notices: [notice, ...(p.notices ?? [])].slice(0, 40) }, data.by, `issued ${a.ref} for approval`, "design");
    });
    actions.record(pid, { kind: "approval-issued", refId: id, title: data.title.trim(), by: data.by });
    return id;
  },
  signApproval(pid: string, aid: string, by: string, signatureDataUri?: string) {
    patchProject(pid, (p) => {
      const a = (p.approvals ?? []).find((x) => x.id === aid);
      return logActivity({ ...p, approvals: (p.approvals ?? []).map((x) => x.id === aid ? { ...x, status: "signed" as const, signedBy: by, signedOn: nowISO(), signatureDataUri } : x) }, by, `signed ${a?.ref ?? "an approval"}`, "design");
    });
    const a = state.projects.find((x) => x.id === pid)?.approvals?.find((x) => x.id === aid);
    actions.record(pid, { kind: "approval-signed", refId: aid, title: a ? `${a.ref} — ${a.title}` : "Drawing approval", by });
  },
  declineApproval(pid: string, aid: string, by: string, note?: string) {
    patchProject(pid, (p) => {
      const a = (p.approvals ?? []).find((x) => x.id === aid);
      return logActivity({ ...p, approvals: (p.approvals ?? []).map((x) => x.id === aid ? { ...x, status: "declined" as const, signedBy: by, signedOn: nowISO(), declineNote: note?.trim() || undefined } : x) }, by, `declined ${a?.ref ?? "an approval"}`, "design");
    });
    const a = state.projects.find((x) => x.id === pid)?.approvals?.find((x) => x.id === aid);
    actions.record(pid, { kind: "approval-declined", refId: aid, title: a ? `${a.ref} — ${a.title}` : "Drawing approval", by });
  },
  removeApproval(pid: string, aid: string) {
    patchProject(pid, (p) => ({ ...p, approvals: (p.approvals ?? []).filter((a) => a.id !== aid) }));
  },

  /* ---- preliminary construction budget: the sizing conversation ----
     The studio owns the rate; the client may move the budget until it's locked.
     Whatever it settles at becomes the project budget the design fee reads. */
  setCostBrief(pid: string, brief: { budget: number; quality: "standard" | "premium" | "bespoke"; unit: "sqft" | "sqm"; ratePerUnit: number; rateOverride?: boolean; by: string; isClient?: boolean }) {
    patchProject(pid, (p) => {
      if (p.costBrief?.locked) return p;                 // agreed — nobody moves it
      const budget = Math.max(0, Math.round(brief.budget) || 0);
      const cb: import("./types").CostBrief = {
        budget, currency: p.billing?.currency ?? p.currency, quality: brief.quality, unit: brief.unit,
        ratePerUnit: brief.ratePerUnit, rateOverride: brief.rateOverride || undefined, updatedOn: nowISO(), updatedBy: brief.by,
        clientTouchedOn: brief.isClient ? nowISO() : p.costBrief?.clientTouchedOn,
      };
      /* The construction budget IS the basis of the fee, so it lands on the
         project too — and on the fee configuration, and through it on a
         payment schedule built from the stages — otherwise the estimator and
         the client's fee page would quote off a stale figure. */
      const np: Project = followFee({ ...p, costBrief: cb, budget, estimate: p.estimate && !p.feeLock ? { ...p.estimate, budget, currency: cb.currency } : p.estimate });
      if (brief.isClient) {
        void notify({
          to: STUDIO.email, subject: `Budget adjusted — ${p.name}`, heading: "The client moved the budget",
          body: `${brief.by} set a preliminary construction budget of ${money(budget, cb.currency)} at ${brief.quality} finish (${money(brief.ratePerUnit, cb.currency)}/${brief.unit === "sqm" ? "m²" : "ft²"}).`,
          cta: { label: "Open the project", url: `https://${STUDIO.domain}/studio/${p.id}/costbrief` },
        });
      }
      return logActivity(np, brief.by, `set the preliminary construction budget to ${money(budget, cb.currency)}`, "payment");
    });
  },
  setCostBriefLocked(pid: string, locked: boolean, by: string) {
    patchProject(pid, (p) => p.costBrief
      ? logActivity({ ...p, costBrief: { ...p.costBrief, locked } }, by, locked ? "locked the preliminary budget" : "reopened the preliminary budget", "payment")
      : p);
  },
  /** Adopt a fitted scope as the actual accommodation schedule. */
  applyFittedSpaces(pid: string, spaces: import("./types").SpaceReq[], by: string) {
    patchProject(pid, (p) => logActivity({ ...p, brief: { ...p.brief, spaces, updatedOn: realToday() } }, by, "resized the accommodation schedule to the budget", "brief"));
  },
  /** Studio-only: the currency the construction rates are written in. */
  setBuildRateCurrency(ccy: string) {
    const c = ccy.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(c)) return;
    commit({ ...state, rateCard: { ...state.rateCard, buildRateCurrency: c } });
  },
  /** Studio-only: units of a currency per 1 USD. */
  setFxRate(ccy: string, perUsd: number) {
    const c = ccy.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(c) || c === "USD") return;
    const fx = { ...(state.rateCard.fx ?? {}) };
    if (perUsd > 0) fx[c] = perUsd; else delete fx[c];
    commit({ ...state, rateCard: { ...state.rateCard, fx } });
  },
  /** Studio-only: the fee before discount, shown to the client beside the agreed fee. */
  setBillingFullFee(pid: string, amount: number) {
    patchProject(pid, (p) => p.billing
      ? logActivity({ ...p, billing: { ...p.billing, fullFee: amount > 0 ? Math.round(amount) : undefined } }, STUDIO.principal, amount > 0 ? `recorded the fee before discount as ${money(amount, p.billing.currency)}` : "cleared the fee before discount", "payment")
      : p);
  },
  /** Studio-only: the construction rate for a building type. */
  setBuildRate(type: string, patch: Partial<import("./buildCost").BuildRate>) {
    const rates = state.rateCard.buildRates ?? [];
    const next = rates.some((r) => r.type === type)
      ? rates.map((r) => r.type === type ? { ...r, ...patch } : r)
      : [...rates, { type, unit: "sqft" as const, standard: 0, premium: 0, bespoke: 0, ...patch }];
    commit({ ...state, rateCard: { ...state.rateCard, buildRates: next } });
  },

  /* ---- nested films: the studio's motion work, shown inside the portal ---- */
  addFilm(pid: string, data: { title: string; subtitle?: string; url: string; note?: string; autoplay?: boolean; by: string; share?: boolean }) {
    patchProject(pid, (p) => {
      const url = data.url.trim();
      if (!url) return p;
      // adding the same film twice is a slip, not an intention
      if ((p.films ?? []).some((f) => f.url === url)) return p;
      const share = data.share ?? false;
      const film: import("./types").ProjectFilm = {
        id: uid("film-"), title: data.title.trim() || "Film", subtitle: data.subtitle?.trim() || undefined,
        url, note: data.note?.trim() || undefined, autoplay: data.autoplay,
        addedOn: nowISO(), addedBy: data.by, sharedWithClient: share,
      };
      const films = [...(p.films ?? []), film];
      // adding and showing is one decision, so it's one write — no read-back
      if (!share) return logActivity({ ...p, films }, data.by, `nested “${film.title}” in the project`, "design");
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `“${film.title}” is ready to watch`, body: film.subtitle || film.note || "A short film about the design." };
      void notify({
        to: p.client.email, subject: `${film.title} — ${p.name}`, heading: film.title,
        body: `${STUDIO.name} has added a short film to your project${film.note ? `. ${film.note}` : "."}`,
        cta: { label: "Watch it", url: `https://${STUDIO.domain}/portal/${p.id}/film` },
      });
      return logActivity({ ...p, films, notices: [notice, ...(p.notices ?? [])].slice(0, 40) }, data.by, `nested “${film.title}” and showed it to the client`, "design");
    });
  },
  updateFilm(pid: string, fid: string, patch: Partial<import("./types").ProjectFilm>) {
    patchProject(pid, (p) => ({ ...p, films: (p.films ?? []).map((f) => f.id === fid ? { ...f, ...patch } : f) }));
  },
  removeFilm(pid: string, fid: string) {
    patchProject(pid, (p) => ({ ...p, films: (p.films ?? []).filter((f) => f.id !== fid) }));
  },
  /** Show it to the client — or take it back down. Sharing raises a notice so it
   *  doesn't sit unnoticed in a tab they never open. */
  shareFilm(pid: string, fid: string, share: boolean, by: string) {
    patchProject(pid, (p) => {
      const film = (p.films ?? []).find((f) => f.id === fid);
      if (!film) return p;
      const films = (p.films ?? []).map((f) => f.id === fid ? { ...f, sharedWithClient: share } : f);
      if (!share) return { ...p, films };
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `“${film.title}” is ready to watch`, body: film.subtitle || film.note || "A short film about the design." };
      void notify({
        to: p.client.email, subject: `${film.title} — ${p.name}`, heading: film.title,
        body: `${STUDIO.name} has added a short film to your project${film.note ? `. ${film.note}` : "."}`,
        cta: { label: "Watch it", url: `https://${STUDIO.domain}/portal/${p.id}/film` },
      });
      return logActivity({ ...p, films, notices: [notice, ...(p.notices ?? [])].slice(0, 40) }, by, `shared “${film.title}” with the client`, "design");
    });
  },

  /* ---- the concept presentation: slides + the options to choose between ---- */
  ensureDeck(pid: string, by: string) {
    patchProject(pid, (p) => p.deck ? p : {
      ...p,
      deck: {
        id: uid("deck-"), title: `${p.name} — Design Concept`, subtitle: p.location || undefined,
        slides: [{ id: uid("sl-"), kind: "cover" as const, title: `${p.name} — Design Concept`, body: `Prepared for ${p.client?.name ?? "the client"} by ${STUDIO.name}.`, layout: "full" as const }],
        options: [], status: "draft" as const, kind: "concept" as const, createdOn: nowISO(), updatedOn: nowISO(),
      },
    });
  },
  /** Replace the deck wholesale — used by the AI drafter. Keeps any client
   *  responses already given against options that survive. */
  setDeck(pid: string, deck: Partial<import("./types").ConceptDeck>, by: string) {
    patchProject(pid, (p) => {
      const prev = p.deck;
      const keptResponses = new Map((prev?.options ?? []).map((o) => [o.ref, o.response]));
      const options = (deck.options ?? prev?.options ?? []).map((o) => ({ ...o, response: o.response ?? keptResponses.get(o.ref) }));
      const next: import("./types").ConceptDeck = {
        id: prev?.id ?? uid("deck-"),
        title: deck.title ?? prev?.title ?? `${p.name} — Design Concept`,
        subtitle: deck.subtitle ?? prev?.subtitle,
        slides: deck.slides ?? prev?.slides ?? [],
        options,
        status: prev?.status ?? "draft",
        createdOn: prev?.createdOn ?? nowISO(),
        updatedOn: nowISO(),
        issuedOn: prev?.issuedOn,
        notes: prev?.notes,
        sources: deck.sources ?? prev?.sources,
        readFiles: deck.readFiles ?? prev?.readFiles,
      };
      const from = next.readFiles?.length ? ` from ${next.readFiles.join(", ")}` : "";
      return logActivity({ ...p, deck: next }, by, `drafted the design concept presentation${from}`, "design");
    });
  },
  updateSlide(pid: string, sid: string, patch: Partial<import("./types").DeckSlide>) {
    patchProject(pid, (p) => p.deck
      ? { ...p, deck: { ...p.deck, updatedOn: nowISO(), slides: p.deck.slides.map((s) => s.id === sid ? { ...s, ...patch, aiDrafted: patch.body !== undefined || patch.title !== undefined ? false : s.aiDrafted } : s) } }
      : p);
  },
  addSlide(pid: string, kind: import("./types").SlideKind, afterId?: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const slide: import("./types").DeckSlide = { id: uid("sl-"), kind, title: kind === "options" ? "The options" : "New slide", body: "", layout: kind === "image" ? "full" : "split" };
      const i = afterId ? p.deck.slides.findIndex((s) => s.id === afterId) : p.deck.slides.length - 1;
      const slides = [...p.deck.slides];
      slides.splice(i + 1, 0, slide);
      return { ...p, deck: { ...p.deck, slides, updatedOn: nowISO() } };
    });
  },
  /** The client accepts the presentation, or asks for changes. Recorded on the
   *  deck itself so a proposal with no options can still be signed off. */
  respondToDeck(pid: string, status: "accepted" | "changes", by: string, comment?: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const acceptance = { status, comment: comment?.trim() || undefined, by, on: nowISO() };
      const what = status === "accepted" ? "accepted" : "asked for changes to";
      void notify({
        to: STUDIO.email,
        subject: `${by} ${what} — ${p.name}`,
        heading: `${by} ${what} the ${p.deck.stage ?? "design"} presentation`,
        body: comment?.trim()
          ? `"${comment.trim()}"`
          : status === "accepted"
          ? "No comments were left — they are happy for it to proceed."
          : "No detail was left; follow it up with them.",
        cta: { label: "Open the presentation", url: `https://${STUDIO.domain}/studio/${p.id}/concept` },
      });
      return logActivity({ ...p, deck: { ...p.deck, acceptance } }, by, `${what} the presentation`, "design");
    });
  },
  /** Open the presentation with a film. It goes straight after the cover, because
   *  the film is what the client should meet first — the drawings land better
   *  once they've seen the thinking move. */
  setOpeningFilm(pid: string, filmId: string | null, by: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const rest = p.deck.slides.filter((s) => s.kind !== "film");
      if (!filmId) return { ...p, deck: { ...p.deck, slides: rest, updatedOn: nowISO() } };
      const film = (p.films ?? []).find((f) => f.id === filmId);
      if (!film) return p;
      const slide: import("./types").DeckSlide = {
        id: uid("sl-"), kind: "film", title: film.title, body: film.note || film.subtitle || "", filmId, layout: "full",
      };
      const coverAt = rest.findIndex((s) => s.kind === "cover");
      const slides = [...rest];
      slides.splice(coverAt + 1, 0, slide);
      return logActivity({ ...p, deck: { ...p.deck, slides, updatedOn: nowISO() } }, by, `opened the concept with “${film.title}”`, "design");
    });
  },
  /** Turn a passage the assistant quoted but didn't use into a slide of its own —
   *  the slide and the "used" mark land in one write, so no stale read between. */
  slideFromSource(pid: string, sourceId: string, by: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const src = (p.deck.sources ?? []).find((s) => s.id === sourceId);
      if (!src) return p;
      const title = src.topic ? src.topic.charAt(0).toUpperCase() + src.topic.slice(1) : "From the brief";
      const slide: import("./types").DeckSlide = {
        id: uid("sl-"), kind: "statement", title, layout: "split",
        body: `“${src.quote}”${src.where ? `\n\n— ${src.where}` : ""}`,
        source: { quote: src.quote, where: src.where },
      };
      return logActivity({
        ...p,
        deck: {
          ...p.deck,
          slides: [...p.deck.slides, slide],
          sources: (p.deck.sources ?? []).map((s) => s.id === sourceId ? { ...s, usedFor: title } : s),
          updatedOn: nowISO(),
        },
      }, by, `added a concept slide from the client's document`, "design");
    });
  },
  moveSlide(pid: string, sid: string, dir: -1 | 1) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const slides = [...p.deck.slides];
      const i = slides.findIndex((s) => s.id === sid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= slides.length) return p;
      [slides[i], slides[j]] = [slides[j], slides[i]];
      return { ...p, deck: { ...p.deck, slides, updatedOn: nowISO() } };
    });
  },
  removeSlide(pid: string, sid: string) {
    patchProject(pid, (p) => p.deck ? { ...p, deck: { ...p.deck, slides: p.deck.slides.filter((s) => s.id !== sid), updatedOn: nowISO() } } : p);
  },
  /* ---- design options ---- */
  addOption(pid: string, data?: Partial<import("./types").DesignOption>) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const used = p.deck.options.map((o) => o.ref);
      const ref = data?.ref ?? "ABCDEFGH".split("").find((c) => !used.includes(c)) ?? String(used.length + 1);
      const opt: import("./types").DesignOption = {
        id: uid("opt-"), ref, name: data?.name ?? `Option ${ref}`, thesis: data?.thesis ?? "",
        description: data?.description ?? "", images: data?.images ?? [],
        strengths: data?.strengths ?? [], tradeoffs: data?.tradeoffs ?? [],
        area: data?.area, costNote: data?.costNote,
      };
      return { ...p, deck: { ...p.deck, options: [...p.deck.options, opt], updatedOn: nowISO() } };
    });
  },
  updateOption(pid: string, oid: string, patch: Partial<import("./types").DesignOption>) {
    patchProject(pid, (p) => p.deck
      ? { ...p, deck: { ...p.deck, updatedOn: nowISO(), options: p.deck.options.map((o) => o.id === oid ? { ...o, ...patch } : o) } }
      : p);
  },
  removeOption(pid: string, oid: string) {
    patchProject(pid, (p) => p.deck ? { ...p, deck: { ...p.deck, options: p.deck.options.filter((o) => o.id !== oid), updatedOn: nowISO() } } : p);
  },
  /** The client's verdict on one option. Choosing a preferred one demotes any
   *  other option that was previously preferred — only one can lead. */
  respondToOption(pid: string, oid: string, choice: "preferred" | "considering" | "no", by: string, comment?: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const opt = p.deck.options.find((o) => o.id === oid);
      const options = p.deck.options.map((o) => {
        if (o.id === oid) return { ...o, response: { choice, comment: comment?.trim() || undefined, by, on: nowISO() } };
        if (choice === "preferred" && o.response?.choice === "preferred") return { ...o, response: { ...o.response, choice: "considering" as const } };
        return o;
      });
      void notify({
        to: STUDIO.email, subject: `Concept response — ${p.name}`, heading: `${by} responded to the concept`,
        body: `${by} marked ${opt?.name ?? "an option"} as "${choice}"${comment ? `: "${comment}"` : "."}`,
        cta: { label: "Open the concept", url: `https://${STUDIO.domain}/studio/${p.id}/concept` },
      });
      return logActivity({ ...p, deck: { ...p.deck, options, updatedOn: nowISO() } }, by, `marked ${opt?.name ?? "an option"} as ${choice}`, "feedback");
    });
  },
  /** A comment on the deck, or on one slide / option within it. */
  addDeckNote(pid: string, data: { slideId?: string; optionId?: string; body: string; by: string; author: "client" | "studio" }) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const note: import("./types").DeckNote = { id: uid("dn-"), slideId: data.slideId, optionId: data.optionId, body: data.body.trim(), by: data.by, on: nowISO(), author: data.author };
      if (data.author === "client") {
        void notify({ to: STUDIO.email, subject: `Concept comment — ${p.name}`, heading: "New comment on the concept", body: `${data.by}: "${note.body}"`, cta: { label: "Open the concept", url: `https://${STUDIO.domain}/studio/${p.id}/concept` } });
      }
      return logActivity({ ...p, deck: { ...p.deck, notes: [...(p.deck.notes ?? []), note], updatedOn: nowISO() } }, data.by, "commented on the concept presentation", "feedback");
    });
  },
  replyDeckNote(pid: string, nid: string, body: string, by: string) {
    patchProject(pid, (p) => p.deck
      ? { ...p, deck: { ...p.deck, notes: (p.deck.notes ?? []).map((n) => n.id === nid ? { ...n, reply: { body: body.trim(), by, on: nowISO() } } : n) } }
      : p);
  },
  issueDeck(pid: string, by: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: "The design concept is ready", body: `${p.deck.options.length ? `${p.deck.options.length} options to consider.` : "Ready for your review."}` };
      const to = p.client.email?.trim();
      /* Record what actually happened. Without an address there is nothing to
         send, and pretending otherwise is how a client ends up never told. */
      if (!to) {
        actions.setDeckNotify(pid, { result: "failed", error: "no email address on file for this client", on: nowISO() });
      } else {
        void notify({
          to, subject: `Design concept — ${p.name}`, heading: p.deck.title,
          body: `${STUDIO.name} has prepared the design concept for ${p.name}.${p.deck.options.length ? ` There ${p.deck.options.length === 1 ? "is one option" : `are ${p.deck.options.length} options`} to look through. Open it in your portal, watch it through, and mark the one you'd like us to develop — there's a box beside each option to tell us why, and you can comment on any part of it.` : " Open it in your portal to read it through and comment on any part of it."}`,
          cta: { label: "Open it in your portal", url: `https://${STUDIO.domain}/portal/${p.id}/concept` },
        }).then((result) => actions.setDeckNotify(pid, { to, result, error: result === "delivered" ? undefined : lastNotifyError(), on: nowISO() }));
      }
      return logActivity({ ...p, deck: { ...p.deck, status: "issued", issuedOn: nowISO() }, notices: [notice, ...(p.notices ?? [])].slice(0, 40) }, by, "presented the design concept to the client", "design");
    });
  },
  setDeckNotify(pid: string, n: NonNullable<import("./types").ConceptDeck["notify"]>) {
    patchProject(pid, (p) => p.deck ? { ...p, deck: { ...p.deck, notify: n } } : p);
  },
  /** Send the "it's ready" email again, to the address on file or a one-off. */
  resendDeckEmail(pid: string, to?: string) {
    const p = getState().projects.find((x) => x.id === pid);
    if (!p?.deck) return;
    const addr = (to ?? p.client.email ?? "").trim();
    if (!addr) { actions.setDeckNotify(pid, { result: "failed", error: "no email address on file for this client", on: nowISO() }); return; }
    void notify({
      to: addr, subject: `Design concept — ${p.name}`, heading: p.deck.title,
      body: `${STUDIO.name} has prepared the design concept for ${p.name}. Open it in your portal, watch it through, and mark the option you'd like us to develop.`,
      cta: { label: "Open it in your portal", url: `https://${STUDIO.domain}/portal/${p.id}/concept` },
    }).then((result) => actions.setDeckNotify(pid, { to: addr, result, error: result === "delivered" ? undefined : lastNotifyError(), on: nowISO() }));
  },

  /** Add a batch of renders as slides — one image each, captioned by file name
   *  until the architect renames them. */
  addRenderSlides(pid: string, images: { url: string; title?: string; video?: boolean; name?: string }[]) {
    patchProject(pid, (p) => {
      if (!p.deck || !images.length) return p;
      const slides = images.map((im) => ({
        id: uid("sl-"), kind: "image" as const, title: im.title?.trim() || "Untitled view",
        body: "", layout: "full" as const,
        ...(im.video ? { video: im.url, videoName: im.name, images: [] } : { images: [im.url] }),
      }));
      return { ...p, deck: { ...p.deck, slides: [...p.deck.slides, ...slides], updatedOn: nowISO() } };
    });
  },
  /** Close the live presentation. It moves into the archive — the client keeps
   *  their copy and their responses; it just stops being the current one. */
  closeDeck(pid: string, by: string) {
    patchProject(pid, (p) => {
      if (!p.deck) return p;
      const closed = { ...p.deck, closedOn: nowISO() };
      return logActivity(
        { ...p, deck: undefined, deckArchive: [closed, ...(p.deckArchive ?? [])] },
        by, `closed the ${closed.stage ?? "concept"} presentation`, "design",
      );
    });
  },
  /** Start a presentation for a later stage. Any live one is closed first, so
   *  there is only ever one current presentation to respond to. */
  startDeck(pid: string, stage: string, by: string, kind: "concept" | "renders" | "design" = "concept") {
    patchProject(pid, (p) => {
      const archive = p.deck ? [{ ...p.deck, closedOn: nowISO() }, ...(p.deckArchive ?? [])] : (p.deckArchive ?? []);
      const deck: import("./types").ConceptDeck = {
        id: uid("deck-"), title: `${p.name} — ${stage}`, subtitle: p.location || undefined, stage,
        slides: [{ id: uid("sl-"), kind: "cover" as const, title: `${p.name} — ${stage}`, body: `Prepared for ${p.client?.name ?? "the client"} by ${STUDIO.name}.`, layout: "full" as const }],
        options: [], status: "draft" as const, createdOn: nowISO(), updatedOn: nowISO(),
      };
      return logActivity({ ...p, deck, deckArchive: archive }, by, `started the ${stage} presentation`, "design");
    });
  },
  /** Put an archived presentation back as the live one — undo a premature close. */
  reopenDeck(pid: string, deckId: string, by: string) {
    patchProject(pid, (p) => {
      const found = (p.deckArchive ?? []).find((d) => d.id === deckId);
      if (!found) return p;
      const rest = (p.deckArchive ?? []).filter((d) => d.id !== deckId);
      const archive = p.deck ? [{ ...p.deck, closedOn: nowISO() }, ...rest] : rest;
      const { closedOn, ...live } = found;
      return logActivity({ ...p, deck: live as import("./types").ConceptDeck, deckArchive: archive }, by, `re-opened the ${found.stage ?? "concept"} presentation`, "design");
    });
  },

  /* ---- tendering: the client's own invitations to price the work ---- */
  /** Open a tender room. Returns the invite so the caller can share the link
   *  immediately — no read-back, the token is minted here. */
  openTender(pid: string, data: { title: string; scope: "whole" | "package" | "selected"; packageId?: string; docIds?: string[]; extraDocs?: import("./types").TenderDoc[]; scopeSections?: import("./types").TenderScopeSection[]; note?: string; deadline?: string; by: string; byRole: "client" | "studio" }): import("./types").TenderInvite {
    const token = safeUUID().replace(/-/g, "") + safeUUID().replace(/-/g, "").slice(0, 8);
    let made = { id: uid("tnd-"), ref: "TND-000", token } as import("./types").TenderInvite;
    patchProject(pid, (p) => {
      const n = (p.tenders ?? []).length ? Math.max(...(p.tenders ?? []).map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      made = {
        ...made, ref: `TND-${String(n).padStart(3, "0")}`,
        title: data.title.trim() || (data.scope === "whole" ? "The whole project" : "A package"),
        scope: data.scope, packageId: data.packageId,
        docIds: data.docIds?.length ? data.docIds : undefined,
        extraDocs: data.extraDocs?.length ? data.extraDocs : undefined,
        scopeSections: data.scopeSections?.length ? data.scopeSections : undefined,
        note: data.note?.trim() || undefined, deadline: data.deadline || undefined,
        createdBy: data.by, createdByRole: data.byRole, createdOn: nowISO(),
        registrations: [],
      };
      /* The studio runs the tender, so it hears when a client opens one. */
      if (data.byRole === "client") {
        void notify({
          to: STUDIO.email, subject: `Tender opened — ${p.name}`, heading: `${data.by} opened a tender`,
          body: `${made.title}${made.deadline ? ` · returns by ${made.deadline}` : ""}. Contractors can register at the link below and will be given their own access code.`,
          cta: { label: "See who registers", url: `https://${STUDIO.domain}/studio/${p.id}/drawings` },
        });
      }
      return logActivity({ ...p, tenders: [...(p.tenders ?? []), made] }, data.by, `opened tender ${made.ref} — ${made.title}`, "report");
    });
    return made;
  },
  /** Adjust a live tender's issued set, uploaded documents or scope of works —
   *  every registered firm sees the change the next time they open the link. */
  updateTenderDetails(pid: string, tid: string, patch: Partial<Pick<import("./types").TenderInvite, "docIds" | "extraDocs" | "scopeSections" | "note" | "deadline" | "scope" | "packageId">>, by: string) {
    patchProject(pid, (p) => {
      const t = (p.tenders ?? []).find((x) => x.id === tid);
      if (!t) return p;
      return logActivity(
        { ...p, tenders: (p.tenders ?? []).map((x) => x.id === tid ? { ...x, ...patch } : x) },
        by, `updated ${t.ref} — scope & documents`, "report");
    });
  },
  closeTender(pid: string, tid: string, by: string) {
    patchProject(pid, (p) => ({
      ...p,
      tenders: (p.tenders ?? []).map((t) => t.id === tid ? { ...t, closedOn: t.closedOn ? undefined : nowISO() } : t),
    }));
  },
  removeTender(pid: string, tid: string) {
    patchProject(pid, (p) => ({ ...p, tenders: (p.tenders ?? []).filter((t) => t.id !== tid) }));
  },
  /** Rule a firm in or out without erasing that they asked. */
  setTendererStatus(pid: string, tid: string, rid: string, status: "registered" | "declined" | "withdrawn") {
    patchProject(pid, (p) => ({
      ...p,
      tenders: (p.tenders ?? []).map((t) => t.id !== tid ? t : {
        ...t, registrations: t.registrations.map((r) => r.id === rid ? { ...r, status } : r),
      }),
    }));
  },

  /* ---- people following the tender ---- */
  addFollower(pid: string, data: { name: string; email: string; note?: string; by: string }) {
    patchProject(pid, (p) => {
      const email = data.email.trim().toLowerCase();
      if (!email || (p.followers ?? []).some((f) => f.email.toLowerCase() === email)) return p;
      const f: import("./types").TenderFollower = {
        id: uid("flw-"), name: data.name.trim() || email, email: data.email.trim(),
        note: data.note?.trim() || undefined, addedBy: data.by, addedOn: nowISO(),
      };
      void notify({
        to: f.email, subject: `Following ${p.name}`, heading: `You're following ${p.name}`,
        body: `${data.by} added you to follow the tender for ${p.name}. You'll be told when firms register and when the tender closes. Nothing is expected of you — this is to keep you in the picture.`,
        cta: { label: "See the project", url: `https://${STUDIO.domain}/portal/${p.id}/overview` },
      });
      return logActivity({ ...p, followers: [...(p.followers ?? []), f] }, data.by, `added ${f.name} to follow the process`, "report");
    });
  },
  removeFollower(pid: string, fid: string) {
    patchProject(pid, (p) => ({ ...p, followers: (p.followers ?? []).filter((f) => f.id !== fid) }));
  },

  /* ---- partner (consultant / contractor) tokened access ---- */
  createPartner(pid: string, data: { name: string; email?: string; kind: "consultant" | "contractor"; disciplines?: string[]; note?: string; by: string }): import("./types").PartnerLink {
    const token = safeUUID().replace(/-/g, "") + safeUUID().replace(/-/g, "").slice(0, 8);
    let made = { id: uid("ptr-"), ref: "PTR-000", token } as import("./types").PartnerLink;
    patchProject(pid, (p) => {
      const n = (p.partners ?? []).length ? Math.max(...(p.partners ?? []).map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      made = {
        ...made, ref: `PTR-${String(n).padStart(3, "0")}`, name: data.name.trim(), email: data.email?.trim() || undefined,
        kind: data.kind, disciplines: data.disciplines?.length ? data.disciplines : undefined, note: data.note?.trim() || undefined,
        createdOn: nowISO(), createdBy: data.by,
      };
      if (made.email) {
        void notify({
          to: made.email, subject: `Project access — ${p.name}`, heading: `${p.name} — your access`,
          body: `${STUDIO.name} has given you access to the drawings and RFIs for ${p.name}. The link is personal to you.${data.note ? `\n\n${data.note}` : ""}`,
          cta: { label: "Open the project", url: `https://${STUDIO.domain}/partner/${token}` },
        });
      }
      return logActivity({ ...p, partners: [made, ...(p.partners ?? [])] }, data.by, `gave ${made.name} partner access (${made.ref})`, "design");
    });
    return made;
  },
  revokePartner(pid: string, id: string, revoked = true) {
    patchProject(pid, (p) => ({ ...p, partners: (p.partners ?? []).map((x) => x.id === id ? { ...x, revoked } : x) }));
  },
  removePartner(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, partners: (p.partners ?? []).filter((x) => x.id !== id) }));
  },

  /* ---- photos pinned to a point on a drawing ---- */
  addPhotoPin(pid: string, data: { sheetCode: string; building?: string; x: number; y: number; url: string; note?: string; by: string }) {
    patchProject(pid, (p) => logActivity({
      ...p, photoPins: [{ id: uid("pin-"), sheetCode: data.sheetCode, building: data.building, x: data.x, y: data.y, url: data.url, note: data.note?.trim() || undefined, by: data.by, on: nowISO() }, ...(p.photoPins ?? [])],
    }, data.by, `pinned a site photo to ${data.sheetCode}`, "design"));
  },
  removePhotoPin(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, photoPins: (p.photoPins ?? []).filter((x) => x.id !== id) }));
  },

  /* ---- cost plan: rates against the BOQ, tracked to a budget ---- */
  setCostPlan(pid: string, plan: import("./types").CostPlan) {
    patchProject(pid, (p) => ({ ...p, costPlan: { ...plan, updatedOn: realToday() } }));
  },
  /** Seed the plan from the BOQ, keeping any rates already entered. */
  seedCostPlanFromBoq(pid: string, by: string) {
    patchProject(pid, (p) => {
      const prev = p.costPlan;
      const byItem = new Map((prev?.lines ?? []).map((l) => [l.boqItemId ?? l.description, l]));
      const lines = (p.boq ?? []).map((b) => {
        const old = byItem.get(b.id) ?? byItem.get(b.description);
        return { id: old?.id ?? uid("cl-"), boqItemId: b.id, description: b.description, qty: b.qty, unit: b.unit, rate: old?.rate ?? 0, tendered: old?.tendered, note: b.note };
      });
      const extra = (prev?.lines ?? []).filter((l) => !l.boqItemId);   // hand-added lines survive
      return logActivity({ ...p, costPlan: { currency: p.billing?.currency ?? p.currency, budget: prev?.budget ?? p.budget, lines: [...lines, ...extra], updatedOn: realToday(), sharedWithClient: prev?.sharedWithClient } }, by, `updated the cost plan (${lines.length} items)`, "design");
    });
  },
  updateCostLine(pid: string, lid: string, patch: Partial<import("./types").CostLine>) {
    patchProject(pid, (p) => p.costPlan ? { ...p, costPlan: { ...p.costPlan, lines: p.costPlan.lines.map((l) => l.id === lid ? { ...l, ...patch } : l), updatedOn: realToday() } } : p);
  },
  addCostLine(pid: string, line: Omit<import("./types").CostLine, "id">) {
    patchProject(pid, (p) => {
      const plan = p.costPlan ?? { currency: p.billing?.currency ?? p.currency, budget: p.budget, lines: [], updatedOn: realToday() };
      return { ...p, costPlan: { ...plan, lines: [...plan.lines, { ...line, id: uid("cl-") }], updatedOn: realToday() } };
    });
  },
  removeCostLine(pid: string, lid: string) {
    patchProject(pid, (p) => p.costPlan ? { ...p, costPlan: { ...p.costPlan, lines: p.costPlan.lines.filter((l) => l.id !== lid) } } : p);
  },
  setCostPlanShared(pid: string, shared: boolean) {
    patchProject(pid, (p) => p.costPlan ? { ...p, costPlan: { ...p.costPlan, sharedWithClient: shared } } : p);
  },
  setCostBudget(pid: string, budget: number) {
    patchProject(pid, (p) => p.costPlan ? { ...p, costPlan: { ...p.costPlan, budget } } : p);
  },

  /* ---- the agent's daily read ---- */
  setAgentRun(pid: string, run: import("./types").AgentRun) {
    patchProject(pid, (p) => ({ ...p, agentRun: run }));
  },
  setAgentActionDone(pid: string, idx: number, done: boolean) {
    patchProject(pid, (p) => p.agentRun ? { ...p, agentRun: { ...p.agentRun, actions: p.agentRun.actions.map((a, i) => i === idx ? { ...a, done } : a) } } : p);
  },

  /* ---- per-person notification preferences, keyed by email so they follow the
     person whether they're a local team record or a server account ---- */
  setNotifyPrefs(email: string, prefs: import("./types").NotifyPrefs) {
    const key = email.trim().toLowerCase();
    if (!key) return;
    commit({ ...state, notifyPrefs: { ...(state.notifyPrefs ?? {}), [key]: prefs } });
  },

  /* ---- multi-currency: a reporting rate, snapshotted onto each invoice ---- */
  setFx(pid: string, costCurrency: string, rate: number) {
    patchProject(pid, (p) => p.billing
      ? { ...p, billing: { ...p.billing, fx: rate > 0 && costCurrency.trim() ? { costCurrency: costCurrency.trim().toUpperCase(), rate, setOn: realToday() } : undefined } }
      : p);
  },

  /* ---- recurring / retainer billing ---- */
  addRetainer(pid: string, data: { label: string; amount: number; cycle: "monthly" | "quarterly"; startOn: string; endOn?: string }) {
    patchProject(pid, (p) => p.billing ? {
      ...p, billing: { ...p.billing, retainers: [...(p.billing.retainers ?? []), {
        id: uid("ret-"), label: data.label.trim() || "Retainer", amount: Math.round(data.amount) || 0,
        cycle: data.cycle, startOn: data.startOn, endOn: data.endOn || undefined, active: true,
      }] },
    } : p);
  },
  setRetainerActive(pid: string, rid: string, active: boolean) {
    patchProject(pid, (p) => p.billing ? { ...p, billing: { ...p.billing, retainers: (p.billing.retainers ?? []).map((r) => r.id === rid ? { ...r, active } : r) } } : p);
  },
  removeRetainer(pid: string, rid: string) {
    patchProject(pid, (p) => p.billing ? { ...p, billing: { ...p.billing, retainers: (p.billing.retainers ?? []).filter((r) => r.id !== rid) } } : p);
  },
  /** Raise the invoice for one cycle of a retainer (manual, or by the daily job). */
  runRetainer(pid: string, rid: string, by: string) {
    patchProject(pid, (p) => {
      const b = p.billing; const r = (b?.retainers ?? []).find((x) => x.id === rid);
      if (!b || !r || !r.active) return p;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + (b.paymentTermsDays ?? 14) * 86_400_000).toISOString().slice(0, 10);
      const period = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
      const inv: Invoice = {
        id: uid("inv-"), ref: `INV-${tail}-${seq}`, title: `${r.label} — ${period}`,
        ...taxed(r.amount, b), issuedOn, dueOn, status: "due",
        ...(b.fx ? { fxRate: b.fx.rate, fxCurrency: b.fx.costCurrency } : {}),
      };
      void notify({ to: p.client.email, subject: `Invoice ${inv.ref} — ${r.label} (${p.name})`, heading: `Invoice ${inv.ref}`, body: `${r.label} for ${period} — ${money(inv.amount, b.currency)}, due ${dueOn}.`, cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` } });
      return logActivity({
        ...p, invoices: [inv, ...p.invoices],
        billing: { ...b, retainers: (b.retainers ?? []).map((x) => x.id === rid ? { ...x, lastRunOn: issuedOn } : x) },
      }, by, `raised ${inv.ref} — ${r.label}`, "payment");
    });
  },

  /* ---- scope-creep sentinel ---- */
  setScopeFlags(pid: string, flags: import("./types").ScopeFlag[]) {
    patchProject(pid, (p) => ({ ...p, scopeFlags: flags }));
  },
  dismissScopeFlag(pid: string, fid: string) {
    patchProject(pid, (p) => ({ ...p, scopeFlags: (p.scopeFlags ?? []).map((f) => f.id === fid ? { ...f, dismissed: true } : f) }));
  },
  linkScopeFlagVariation(pid: string, fid: string, variationId: string) {
    patchProject(pid, (p) => ({ ...p, scopeFlags: (p.scopeFlags ?? []).map((f) => f.id === fid ? { ...f, variationId } : f) }));
  },

  /* ---- engagement (signable appointment) ---- */
  ensureEngagement(pid: string) {
    patchProject(pid, (p) => p.engagement ? p : { ...p, engagement: defaultEngagement() });
  },
  updateLimitations(pid: string, limitations: string[]) {
    patchProject(pid, (p) => ({ ...p, engagement: { ...(p.engagement ?? defaultEngagement()), limitations } }));
  },
  updateEngagementTerms(pid: string, terms: string) {
    patchProject(pid, (p) => ({ ...p, engagement: { ...(p.engagement ?? defaultEngagement()), terms } }));
  },
  issueEngagement(pid: string) {
    patchProject(pid, (p) => logActivity({ ...p, engagement: { ...(p.engagement ?? defaultEngagement()), status: "issued", issuedOn: realToday() } }, "Studio", "issued the engagement for signature", "brief"));
    actions.record(pid, { kind: "engagement-issued", title: "Letter of engagement — issued for signature", by: STUDIO.principal });
  },
  signEngagement(pid: string, name: string, signatureDataUri: string) {
    patchProject(pid, (p) => logActivity({ ...p, engagement: { ...(p.engagement ?? defaultEngagement()), status: "signed", signedBy: name, signedOn: realToday(), signatureDataUri } }, name, "digitally signed the engagement", "brief"));
    actions.record(pid, { kind: "engagement-signed", title: "Letter of engagement — signed", by: name });
  },

  /* ---- deliverables / IFT package ---- */
  setDeliverableStatus(pid: string, did: string, status: "todo" | "doing" | "issued") {
    patchProject(pid, (p) => ({ ...p, deliverables: (p.deliverables ?? []).map((d) => d.id === did ? { ...d, status, issuedOn: status === "issued" ? realToday() : undefined } : d) }));
  },
  addDeliverable(pid: string, d: { code: string; title: string; discipline: string; phase: "Conceptual" | "Design Development" | "IFT" }) {
    patchProject(pid, (p) => ({ ...p, deliverables: [...(p.deliverables ?? []), { id: uid("dl-"), ...d, status: "todo" as const }] }));
  },
  removeDeliverable(pid: string, did: string) {
    patchProject(pid, (p) => ({ ...p, deliverables: (p.deliverables ?? []).filter((d) => d.id !== did) }));
  },
  /* ---- registry management: archive (recoverable) and bulk delete ---- */
  /** Archive or restore sheets — archived stay in the record but drop out of the
   *  working register and the client's view. Recoverable, unlike delete. */
  setDeliverablesArchived(pid: string, ids: string[], archived: boolean) {
    const set = new Set(ids);
    patchProject(pid, (p) => {
      const n = (p.deliverables ?? []).filter((d) => set.has(d.id)).length;
      if (!n) return p;
      return logActivity(
        { ...p, deliverables: (p.deliverables ?? []).map((d) => set.has(d.id) ? { ...d, archived } : d) },
        STUDIO.principal, `${archived ? "archived" : "restored"} ${n} sheet${n === 1 ? "" : "s"}`, "design");
    });
  },
  /** Permanently remove sheets from the register. */
  removeDeliverables(pid: string, ids: string[]) {
    const set = new Set(ids);
    patchProject(pid, (p) => {
      const n = (p.deliverables ?? []).filter((d) => set.has(d.id)).length;
      if (!n) return p;
      return logActivity({ ...p, deliverables: (p.deliverables ?? []).filter((d) => !set.has(d.id)) }, STUDIO.principal, `removed ${n} sheet${n === 1 ? "" : "s"} from the register`, "design");
    });
  },
  /* ---- full-quality drawings: scan a set, revise a sheet, snapshot the set ---- */
  /** Merge a scanned/uploaded drawing set into the register. A sheet whose
   *  number already exists is auto-revisioned (a new issue at the next rev);
   *  a new number becomes a new sheet. The original file and preview travel
   *  with each issue, so the register carries the full-quality drawings. */
  importDrawingSet(pid: string, by: string, sheets: {
    code: string; title: string; discipline: string; phase: "Conceptual" | "Design Development" | "IFT";
    revision?: string; previewUrl?: string; fileUrl?: string; fileName?: string; page?: number; fileSize?: number; fileHash?: string;
  }[], building?: string) {
    patchProject(pid, (p) => {
      let deliverables = [...(p.deliverables ?? [])];
      const bld = building?.trim() || undefined;
      // on a multi-building project a sheet is identified by building + number,
      // so matching (and the supersede below) is scoped to the target building.
      const sameBuilding = (d: Deliverable) => (d.building || undefined) === bld;
      /* A register populated by upload supersedes the auto-generated base set:
         the placeholder template sheets (never drawn — no drawing, no preview,
         no revision history) give way to the real uploaded register. Scoped to
         this building so uploading one building doesn't wipe another's set. */
      if (sheets.length) {
        const placeholder = (d: Deliverable) => !d.fileUrl && !d.previewUrl && !(d.issues?.length);
        deliverables = deliverables.filter((d) => !(placeholder(d) && sameBuilding(d)));
      }
      let added = 0, revised = 0;
      const on = realToday();
      for (const s of sheets) {
        const code = s.code.trim().toUpperCase();
        if (!code) continue;
        const i = deliverables.findIndex((d) => d.code.trim().toUpperCase() === code && sameBuilding(d));
        if (i === -1) {
          const rev = s.revision?.trim() || "P1";
          const issue: import("./types").DrawingIssue = { rev, issuedOn: on, by, fileUrl: s.fileUrl, fileName: s.fileName, previewUrl: s.previewUrl, page: s.page };
          deliverables.push({
            id: uid("dl-"), code, title: s.title.trim() || "Untitled sheet", discipline: s.discipline, phase: s.phase,
            building: bld, status: "issued", kind: "drawing", revision: rev, issuedOn: on,
            previewUrl: s.previewUrl, fileUrl: s.fileUrl, fileName: s.fileName, page: s.page, fileSize: s.fileSize, fileHash: s.fileHash, issues: [issue],
          });
          added++;
        } else {
          const d = deliverables[i];
          const curRev = d.revision || d.issues?.[d.issues.length - 1]?.rev || "P1";
          const rev = bumpRev(curRev);
          const issue: import("./types").DrawingIssue = { rev, issuedOn: on, by, note: "Reissued from uploaded set", fileUrl: s.fileUrl, fileName: s.fileName, previewUrl: s.previewUrl, page: s.page };
          deliverables[i] = {
            ...d, title: s.title.trim() || d.title, phase: s.phase, status: "issued", revision: rev, issuedOn: on,
            previewUrl: s.previewUrl ?? d.previewUrl, fileUrl: s.fileUrl ?? d.fileUrl, fileName: s.fileName ?? d.fileName, page: s.page ?? d.page,
            fileSize: s.fileSize ?? d.fileSize, fileHash: s.fileHash ?? d.fileHash,
            issues: [...(d.issues ?? (d.revision ? [{ rev: d.revision, issuedOn: d.issuedOn ?? on, previewUrl: d.previewUrl, fileUrl: d.fileUrl }] : [])), issue],
          };
          revised++;
        }
      }
      const base = (p.deliverables ?? []).length;
      const superseded = base > 0 && base > deliverables.length - added;
      const buildings = bld && !(p.buildings ?? []).includes(bld) ? [...(p.buildings ?? []), bld] : p.buildings;
      const notice: ClientNotice = {
        id: uid(), on: nowISO(), kind: "design",
        title: `New drawings issued${bld ? ` — ${bld}` : ""}`,
        body: `${added} new sheet${added === 1 ? "" : "s"}${revised ? `, ${revised} revised` : ""} — open the Drawings room to see them, stage by stage.`,
      };
      return logActivity({ ...p, deliverables, buildings, notices: (added + revised) > 0 ? [notice, ...(p.notices ?? [])].slice(0, 40) : p.notices }, by, `uploaded a drawing set${bld ? ` for ${bld}` : ""} — ${added} new sheet${added === 1 ? "" : "s"}${revised ? `, ${revised} revised` : ""}${superseded ? " (superseded the base register)" : ""}`, "design");
    });
    /* One email per upload, not per sheet: the client hears their set moved, and
       every live partner whose discipline the batch touches hears too. */
    const p = state.projects.find((x) => x.id === pid);
    if (p && sheets.length) {
      const label = `${sheets.length} sheet${sheets.length === 1 ? "" : "s"}`;
      void notify({
        to: p.client.email, subject: `New drawings — ${p.name}`, heading: "New drawings on your project",
        body: `${by} has issued ${label}${building ? ` for ${building}` : ""} on ${p.name}. They're in your portal now, organised by stage — open any sheet to review it, highlight an element and leave a note, or download the stage as a package.`,
        cta: { label: "Open the drawings", url: `https://${STUDIO.domain}/portal/${p.id}/drawings` },
      });
      const discs = new Set(sheets.map((s) => s.discipline));
      for (const ptr of (p.partners ?? []).filter((x) => !x.revoked && x.email)) {
        const mine = !ptr.disciplines?.length || ptr.disciplines.some((d) => discs.has(d));
        if (!mine) continue;
        void notify({
          to: ptr.email!, subject: `Drawings updated — ${p.name}`, heading: "Drawings updated for your discipline",
          body: `${STUDIO.name} has issued ${label} on ${p.name}. Your workspace carries the sheets for your part of the work.`,
          cta: { label: "Open your workspace", url: `https://${STUDIO.domain}/partner/${ptr.token}` },
        });
      }
    }
  },
  /** Reissue one sheet at the next revision, with the new drawing + a note. */
  reviseDeliverable(pid: string, did: string, by: string, data: { previewUrl?: string; fileUrl?: string; fileName?: string; page?: number; note?: string; clouds?: import("./types").DrawingCloud[] }) {
    patchProject(pid, (p) => {
      const d = (p.deliverables ?? []).find((x) => x.id === did);
      if (!d) return p;
      const curRev = d.revision || d.issues?.[d.issues.length - 1]?.rev || "P1";
      const rev = bumpRev(curRev);
      const on = realToday();
      const clouds = data.clouds && data.clouds.length ? data.clouds : undefined;
      const issue: import("./types").DrawingIssue = { rev, issuedOn: on, by, note: data.note?.trim() || undefined, fileUrl: data.fileUrl, fileName: data.fileName, previewUrl: data.previewUrl, page: data.page, clouds };
      const deliverables = (p.deliverables ?? []).map((x) => x.id === did ? {
        ...x, status: "issued" as const, revision: rev, issuedOn: on,
        previewUrl: data.previewUrl ?? x.previewUrl, fileUrl: data.fileUrl ?? x.fileUrl, fileName: data.fileName ?? x.fileName, page: data.page ?? x.page,
        clouds,
        issues: [...(x.issues ?? (x.revision ? [{ rev: x.revision, issuedOn: x.issuedOn ?? on, previewUrl: x.previewUrl, fileUrl: x.fileUrl }] : [])), issue],
      } : x);
      const changed = clouds ? ` — ${clouds.length} change${clouds.length === 1 ? "" : "s"} clouded` : "";
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `${d.code} reissued at ${rev}`, body: data.note?.trim() || `${d.title} has a new revision${clouds ? ` — the changed areas are clouded on the sheet` : ""}.` };
      return logActivity({ ...p, deliverables, notices: [notice, ...(p.notices ?? [])].slice(0, 40) }, by, `reissued ${d.code} at ${rev}${d.revision ? ` — ${d.revision} superseded` : ""}${changed}`, "design");
    });
    /* The client (and the sheet's discipline partners) hear about a reissue —
       building from a superseded drawing is the expensive mistake. */
    const p = state.projects.find((x) => x.id === pid);
    const d = p?.deliverables?.find((x) => x.id === did);
    if (p && d) {
      void notify({
        to: p.client.email, subject: `${d.code} reissued — ${p.name}`, heading: `${d.code} has a new revision`,
        body: `${d.title} was reissued at revision ${d.revision}${data.note?.trim() ? `: "${data.note.trim()}"` : ""}. The changed areas are clouded on the sheet in your portal.`,
        cta: { label: "Open the drawing", url: `https://${STUDIO.domain}/portal/${p.id}/drawings` },
      });
      for (const ptr of (p.partners ?? []).filter((x) => !x.revoked && x.email && (!x.disciplines?.length || x.disciplines.includes(d.discipline)))) {
        void notify({
          to: ptr.email!, subject: `${d.code} reissued — ${p.name}`, heading: `${d.code} superseded`,
          body: `${d.title} is now at revision ${d.revision}. Anything priced or built from the earlier issue should be checked against it.`,
          cta: { label: "Open your workspace", url: `https://${STUDIO.domain}/partner/${ptr.token}` },
        });
      }
    }
  },
  /** Edit a sheet's register details — number, title, discipline, phase, building
   *  and current revision. Configuration, not a reissue: no new revision is cut. */
  editDeliverable(pid: string, did: string, patch: { code?: string; title?: string; discipline?: string; phase?: "Conceptual" | "Design Development" | "IFT"; building?: string; revision?: string }) {
    patchProject(pid, (p) => {
      const d = (p.deliverables ?? []).find((x) => x.id === did);
      if (!d) return p;
      const clean = {
        ...(patch.code !== undefined ? { code: patch.code.trim().toUpperCase() } : {}),
        ...(patch.title !== undefined ? { title: patch.title.trim() } : {}),
        ...(patch.discipline !== undefined ? { discipline: patch.discipline } : {}),
        ...(patch.phase !== undefined ? { phase: patch.phase } : {}),
        ...(patch.building !== undefined ? { building: patch.building.trim() || undefined } : {}),
        ...(patch.revision !== undefined ? { revision: patch.revision.trim() || undefined } : {}),
      };
      return logActivity({ ...p, deliverables: (p.deliverables ?? []).map((x) => x.id === did ? { ...x, ...clean } : x) }, STUDIO.principal, `edited ${d.code} in the register`, "design");
    });
  },
  /** Remove one superseded revision from a sheet's history. The current issue
   *  (last in the chain) can't be removed — reissue or delete the sheet instead. */
  removeDrawingIssue(pid: string, did: string, rev: string) {
    patchProject(pid, (p) => {
      const d = (p.deliverables ?? []).find((x) => x.id === did);
      if (!d || !d.issues || d.issues.length <= 1) return p;
      if (d.issues[d.issues.length - 1].rev === rev) return p;   // never the current one
      return { ...p, deliverables: (p.deliverables ?? []).map((x) => x.id === did ? { ...x, issues: x.issues!.filter((i) => i.rev !== rev) } : x) };
    });
  },
  /** Freeze the whole current register as a numbered set issue — the "overall
   *  revision". Kept as a historical package the client and site can pull. */
  issueDrawingSetSnapshot(pid: string, data: { label: string; note?: string; shareWithClient?: boolean }, by: string) {
    patchProject(pid, (p) => {
      const drawn = (p.deliverables ?? []).filter((d) => (d.kind ?? "drawing") !== "document" || d.previewUrl || d.fileUrl);
      if (!drawn.length) return p;
      const snap: import("./types").DrawingSetIssue = {
        id: uid("set-"), label: data.label.trim() || "Set issue", issuedOn: realToday(), by, note: data.note?.trim() || undefined,
        sharedWithClient: !!data.shareWithClient,
        sheets: drawn.map((d) => ({ code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, revision: d.revision || "P1", building: d.building, previewUrl: d.previewUrl, fileUrl: d.fileUrl, page: d.page })),
      };
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `Drawings issued — ${snap.label}`, body: `${snap.sheets.length} sheets issued as ${snap.label}.` };
      return logActivity(
        { ...p, drawingSets: [snap, ...(p.drawingSets ?? [])], notices: data.shareWithClient ? [notice, ...(p.notices ?? [])].slice(0, 40) : p.notices },
        by, `issued the drawing set — ${snap.label} (${snap.sheets.length} sheets)`, "design");
    });
  },
  shareDrawingSet(pid: string, setId: string, on: boolean) {
    patchProject(pid, (p) => ({ ...p, drawingSets: (p.drawingSets ?? []).map((s) => s.id === setId ? { ...s, sharedWithClient: on } : s) }));
  },
  /* ---- drawing notes: client/studio revision markups + threads ---- */
  /** Mark a revision note on a drawing — optionally with a highlighted region.
   *  Raised by the client or the studio; notifies + emails the other side. */
  addDrawingNote(pid: string, n: { sheetKey: string; code: string; rev?: string; region?: { x: number; y: number; w: number; h: number }; body: string; author: "client" | "studio"; by: string }) {
    patchProject(pid, (p) => {
      if (!n.body.trim()) return p;
      const note: import("./types").DrawingNote = {
        id: uid("dn-"), sheetKey: n.sheetKey, code: n.code, rev: n.rev, region: n.region,
        body: n.body.trim(), author: n.author, by: n.by, on: nowISO(), resolved: false, replies: [],
      };
      const notices = n.author === "client"
        ? [{ id: uid(), on: nowISO(), kind: "design" as const, title: `Note on ${n.code}`, body: `${n.by}: ${note.body.slice(0, 120)}` }, ...(p.notices ?? [])].slice(0, 40)
        : p.notices;
      return logActivity({ ...p, drawingNotes: [note, ...(p.drawingNotes ?? [])], notices },
        n.by, `noted ${n.code}${n.region ? " (highlighted a section)" : ""}`, "design");
    });
    if (n.author === "client") {
      const p = state.projects.find((x) => x.id === pid);
      if (p) void notify({ to: STUDIO.email, subject: `Drawing note on ${n.code} — ${p.name}`,
        heading: `Revision note on ${n.code}`, body: `${n.by} marked a note on ${n.code}:\n\n“${n.body.trim()}”`,
        cta: { label: "Open the drawing", url: `https://${STUDIO.domain}/studio/${p.id}/drawings` } });
    }
  },
  replyDrawingNote(pid: string, noteId: string, data: { author: "client" | "studio"; by: string; body: string }) {
    patchProject(pid, (p) => {
      if (!data.body.trim()) return p;
      return { ...p, drawingNotes: (p.drawingNotes ?? []).map((x) => x.id === noteId
        ? { ...x, replies: [...(x.replies ?? []), { id: uid(), author: data.author, by: data.by, body: data.body.trim(), on: nowISO() }] } : x) };
    });
  },
  resolveDrawingNote(pid: string, noteId: string, resolved: boolean) {
    patchProject(pid, (p) => ({ ...p, drawingNotes: (p.drawingNotes ?? []).map((x) => x.id === noteId ? { ...x, resolved } : x) }));
  },
  /** Purge the empty base-register sheets — the auto-generated placeholder index
   *  (no drawing, no preview, no revision history) that bloats the set once real
   *  drawings have been uploaded. Sheets that carry a drawing are never touched. */
  removeEmptyDrawings(pid: string) {
    patchProject(pid, (p) => {
      const keep = (p.deliverables ?? []).filter((d) => d.fileUrl || d.previewUrl || (d.issues?.length));
      const removed = (p.deliverables ?? []).length - keep.length;
      if (!removed) return p;
      return logActivity({ ...p, deliverables: keep }, STUDIO.principal, `cleared ${removed} empty register sheet${removed === 1 ? "" : "s"}`, "design");
    });
  },
  generateIftPackage(pid: string) {
    patchProject(pid, (p) => logActivity({ ...p, deliverables: iftPackageFor(p.type, p.floors ?? 1) }, "Studio", "generated the IFT drawing package", "design"));
  },
  /* ---- task mind map: the work programme, delegated ---- */
  /** Mirror the work programme onto a Mind Map board — one node per task. */
  async pushProgramToMindmap(pid: string): Promise<{ ok: boolean; reason?: string }> {
    const p = state.projects.find((x) => x.id === pid);
    if (!p) return { ok: false, reason: "project not found" };
    if (!p.program.length) return { ok: false, reason: "there's no work programme to send yet" };
    try {
      const res = await fetch("/api/mindmap/board", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `${p.name} — design programme`,
          tasks: p.program.map((t) => ({
            id: t.id, label: t.name, start: t.start, due: t.end, progress: t.progress, status: t.status,
            notes: `${p.code} · ${t.phase}${t.owner ? ` · ${t.owner}` : ""}`,
          })),
        }),
      });
      const out = await res.json() as { linked?: boolean; reason?: string; sheetId?: string; nodes?: Record<string, string>; url?: string };
      if (!out.linked || !out.sheetId) return { ok: false, reason: out.reason || "the mind map turned it down" };
      patchProject(pid, (x) => logActivity(
        { ...x, mindmap: { sheetId: out.sheetId!, url: out.url || "", nodes: out.nodes ?? {}, pushedAt: nowISO() } },
        "Studio", `sent the work programme to the mind map — ${Object.keys(out.nodes ?? {}).length} tasks`, "design",
      ));
      return { ok: true };
    } catch {
      return { ok: false, reason: "couldn't reach the mind map" };
    }
  },
  /** Release the next drawing stage to the board — Schematic Design first, then
   *  Design Development and Construction Documents as each prior stage is
   *  accepted. Drawings are scheduled and the dates land on the calendar. */
  async sendDrawingStageToMindmap(pid: string): Promise<{ ok: boolean; stage?: string; count?: number; reason?: string }> {
    const p = state.projects.find((x) => x.id === pid);
    if (!p) return { ok: false, reason: "project not found" };
    const { stage, reason } = releasableStage(p);
    if (!stage) return { ok: false, reason: reason || "no stage to send right now" };
    const drawings = (p.deliverables ?? []).filter((d) => d.phase === stage.phase);
    if (!drawings.length) return { ok: false, reason: `there are no ${stage.label} drawings yet — generate the register first` };

    const start = stageStart(p, stage, nowISO().slice(0, 10));
    const scheduled = scheduleStage(drawings, start, stage.weeks);
    const dueById = new Map(scheduled.map((s) => [s.id, s.due]));
    const lastDue = scheduled.reduce((m, s) => (s.due > m ? s.due : m), start);
    try {
      const res = await fetch("/api/mindmap/milestones", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sheetId: p.mindmap?.sheetId,
          title: `${p.name} — drawings`,
          group: `${stage.label} — drawings`,
          milestones: drawings.map((d) => ({ key: d.id, label: `${d.code} · ${d.title}`, due: dueById.get(d.id) || "", notes: `${p.code} · ${stage.label} · ${d.discipline}${d.scale ? ` · ${d.scale}` : ""}` })),
        }),
      });
      const out = await res.json() as { linked?: boolean; reason?: string; sheetId?: string; nodes?: Record<string, string>; url?: string };
      if (!out.linked || !out.sheetId) return { ok: false, reason: out.reason || "the mind map turned it down" };
      const count = Object.keys(out.nodes ?? {}).length;
      patchProject(pid, (x) => {
        const deliverables = (x.deliverables ?? []).map((d) => dueById.has(d.id) ? { ...d, dueOn: dueById.get(d.id) } : d);
        const evTitle = `${stage.label} drawings complete`;
        const schedule = [...(x.schedule ?? []).filter((e) => e.title !== evTitle),
          { id: uid(), title: evTitle, date: lastDue, kind: "milestone" as const, stage: stage.projectStage }]
          .sort((a, b) => +new Date(a.date) - +new Date(b.date));
        const mindmap = {
          sheetId: out.sheetId!, url: out.url || x.mindmap?.url || "",
          nodes: x.mindmap?.nodes ?? {}, milestones: x.mindmap?.milestones,
          stages: { ...(x.mindmap?.stages ?? {}), [stage.key]: { sentAt: nowISO(), nodes: out.nodes ?? {} } },
          pushedAt: x.mindmap?.pushedAt ?? nowISO(), lastProbedAt: x.mindmap?.lastProbedAt,
        };
        return logActivity({ ...x, deliverables, schedule, mindmap }, "Studio", `sent the ${stage.label} drawings to the mind map — ${count} sheets, scheduled through ${lastDue}`, "schedule");
      });
      return { ok: true, stage: stage.label, count };
    } catch {
      return { ok: false, reason: "couldn't reach the mind map" };
    }
  },
  /** Pull completion back from the board — for both the work programme and the
   *  drawing stages. Newly-finished work advances the register and tells the client. */
  async probeMindmap(pid: string): Promise<{ ok: boolean; completed: number; reason?: string }> {
    const p = state.projects.find((x) => x.id === pid);
    if (!p?.mindmap) return { ok: false, completed: 0, reason: "not linked to a board yet" };
    try {
      const res = await fetch(`/api/mindmap/board/${p.mindmap.sheetId}/status`, { credentials: "same-origin" });
      const out = await res.json() as { nodes?: { id: string; status: string; progress: number }[]; error?: string };
      if (out.error || !out.nodes) return { ok: false, completed: 0, reason: out.error || "no answer from the board" };
      const byNode = new Map(out.nodes.map((n) => [n.id, n]));

      const fresh: string[] = [];
      const program = p.program.map((t) => {
        const nodeId = p.mindmap!.nodes[t.id];
        const n = nodeId ? byNode.get(nodeId) : undefined;
        if (!n) return t;
        const status = n.status === "done" ? "done" : n.status === "blocked" ? "blocked" : n.status === "todo" ? "todo" : "doing";
        if (status === "done" && t.status !== "done") fresh.push(t.name);
        return { ...t, status: status as TaskStatus, progress: status === "done" ? 100 : (n.progress ?? t.progress) };
      });

      // drawing stages: mark a sheet issued when its node completes
      let deliverables = p.deliverables ?? [];
      const freshSheets: string[] = [];
      for (const s of Object.values(p.mindmap.stages ?? {})) {
        for (const [delId, nodeId] of Object.entries(s.nodes)) {
          const n = byNode.get(nodeId);
          if (n?.status !== "done") continue;
          const d = deliverables.find((x) => x.id === delId);
          if (d && d.status !== "issued") {
            freshSheets.push(d.code);
            deliverables = deliverables.map((x) => x.id === delId ? { ...x, status: "issued" as const, issuedOn: nowISO().slice(0, 10) } : x);
          }
        }
      }

      const notices: ClientNotice[] = [
        ...fresh.map((name) => ({ id: uid(), on: nowISO(), kind: "task" as const, title: `${name} — complete`, body: "Ticked off on your design programme." })),
        ...(freshSheets.length ? [{ id: uid(), on: nowISO(), kind: "design" as const, title: `${freshSheets.length} drawing${freshSheets.length > 1 ? "s" : ""} issued`, body: `${freshSheets.join(", ")} — now complete on your drawing set.` }] : []),
      ];
      const total = fresh.length + freshSheets.length;
      patchProject(pid, (x) => {
        const next = { ...x, program, deliverables, mindmap: { ...x.mindmap!, lastProbedAt: nowISO() }, notices: [...notices, ...(x.notices ?? [])].slice(0, 40) };
        return total ? logActivity(next, "Mind map", `${total} item${total > 1 ? "s" : ""} completed${freshSheets.length ? ` — ${freshSheets.length} drawings issued` : ""}`, "design") : next;
      });
      return { ok: true, completed: total };
    } catch {
      return { ok: false, completed: 0, reason: "couldn't reach the mind map" };
    }
  },
  /* ---- milestones: planned on the mind map, dated back into the calendar ---- */
  /** Put the programme's milestones on the board for the studio to date. */
  async pushMilestonesToMindmap(pid: string): Promise<{ ok: boolean; count?: number; reason?: string }> {
    const p = state.projects.find((x) => x.id === pid);
    if (!p) return { ok: false, reason: "project not found" };
    if (!milestonesReady(p)) return { ok: false, reason: "the brief has to be approved first — until then the target moves" };
    const seeds = proposedMilestones(p, nowISO().slice(0, 10));
    try {
      const res = await fetch("/api/mindmap/milestones", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sheetId: p.mindmap?.sheetId,
          title: `${p.name} — design programme`,
          group: "Milestones",
          milestones: seeds.map((m) => ({ key: m.key, label: m.title, due: m.date, notes: `${p.code} · ${m.note}` })),
        }),
      });
      const out = await res.json() as { linked?: boolean; reason?: string; sheetId?: string; nodes?: Record<string, string>; url?: string };
      if (!out.linked || !out.sheetId) return { ok: false, reason: out.reason || "the mind map turned it down" };
      const count = Object.keys(out.nodes ?? {}).length;
      patchProject(pid, (x) => logActivity({
        ...x,
        mindmap: {
          sheetId: out.sheetId!, url: out.url || x.mindmap?.url || "",
          nodes: x.mindmap?.nodes ?? {}, milestones: { ...(x.mindmap?.milestones ?? {}), ...(out.nodes ?? {}) },
          pushedAt: x.mindmap?.pushedAt ?? nowISO(), lastProbedAt: x.mindmap?.lastProbedAt,
          milestonesPushedAt: nowISO(), datesPulledAt: x.mindmap?.datesPulledAt,
        },
      }, "Studio", `put ${count} milestones on the mind map to be dated`, "schedule"));
      return { ok: true, count };
    } catch {
      return { ok: false, reason: "couldn't reach the mind map" };
    }
  },
  /** Read the dates the studio set on the map into this project's calendar. */
  async pullMilestoneDates(pid: string): Promise<{ ok: boolean; added?: number; moved?: string[]; reason?: string }> {
    const p = state.projects.find((x) => x.id === pid);
    const mm = p?.mindmap;
    if (!p || !mm?.sheetId || !mm.milestones || !Object.keys(mm.milestones).length) {
      return { ok: false, reason: "no milestones on the mind map yet" };
    }
    try {
      const res = await fetch(`/api/mindmap/board/${mm.sheetId}/status`, { credentials: "same-origin" });
      const out = await res.json() as { nodes?: { id: string; due?: string }[]; error?: string };
      if (out.error || !out.nodes) return { ok: false, reason: out.error || "no answer from the board" };
      const byNode = new Map(out.nodes.map((n) => [n.id, n]));

      const dated = BRIEF_MILESTONES.flatMap((m) => {
        const nodeId = mm.milestones![m.key];
        const due = nodeId ? byNode.get(nodeId)?.due : undefined;
        const date = (due || "").slice(0, 10);
        return date ? [{ key: m.key, title: m.title, date, kind: m.kind, stage: m.stage }] : [];
      });
      if (!dated.length) return { ok: false, reason: "no dates set on the map yet — set them there first" };

      const { schedule, added, moved } = applyMilestoneDates(p.schedule ?? [], dated, () => uid());
      patchProject(pid, (x) => {
        const next = { ...x, schedule, mindmap: { ...x.mindmap!, datesPulledAt: nowISO() } };
        return added || moved.length
          ? logActivity(next, "Mind map", `calendar updated from the mind map — ${added} added${moved.length ? `, ${moved.length} moved` : ""}`, "schedule")
          : next;
      });
      return { ok: true, added, moved };
    } catch {
      return { ok: false, reason: "couldn't reach the mind map" };
    }
  },

  markNoticesSeen(pid: string) {
    patchProject(pid, (p) => ({ ...p, notices: (p.notices ?? []).map((n) => ({ ...n, seen: true })) }));
  },

  /** Record the job the Project Studio created from this project (posted back on handoff). */
  unlinkStudioJob(pid: string, target: "pm" | "bid" | "contract") {
    patchProject(pid, (p) => {
      const links = { ...(p.links ?? {}) };
      delete links[target];
      return { ...p, links };
    });
  },
  setStudioLink(pid: string, target: "pm" | "bid" | "contract", ref: StudioLinkRef) {
    patchProject(pid, (p) => logActivity(
      { ...p, links: { ...p.links, [target]: ref } }, "Studio",
      `linked to the ${target === "pm" ? "Project Manager" : "Bid Manager"} — ${ref.code}${ref.sheets ? ` with ${ref.sheets} sheets` : ""}`, "design",
    ));
  },

  /* ---- mood board & swatches ---- */
  addMoodImage(pid: string, src: string, caption: string, addedBy: "client" | "team", name: string) {
    patchProject(pid, (p) => logActivity({ ...p, moodboard: { images: [{ id: uid("mi-"), src, caption: caption || undefined, addedBy, addedByName: name, addedOn: realToday() }, ...(p.moodboard?.images ?? [])], swatches: p.moodboard?.swatches ?? [] } }, name, "added an image to the mood board", "design"));
  },
  removeMoodImage(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, moodboard: { images: (p.moodboard?.images ?? []).filter((m) => m.id !== id), swatches: p.moodboard?.swatches ?? [] } }));
  },
  addSwatch(pid: string, sw: { name: string; hex: string; material?: string; note?: string }) {
    patchProject(pid, (p) => ({ ...p, moodboard: { images: p.moodboard?.images ?? [], swatches: [...(p.moodboard?.swatches ?? []), { id: uid("sw-"), ...sw }] } }));
  },
  addSwatches(pid: string, sws: { name: string; hex: string; material?: string }[]) {
    patchProject(pid, (p) => {
      const have = new Set((p.moodboard?.swatches ?? []).map((s) => s.name.toLowerCase()));
      const add = sws.filter((s) => s.name && !have.has(s.name.toLowerCase())).map((s) => ({ id: uid("sw-"), name: s.name, hex: s.hex || "#ccc4b6", material: s.material }));
      return logActivity({ ...p, moodboard: { images: p.moodboard?.images ?? [], swatches: [...(p.moodboard?.swatches ?? []), ...add] } }, "Studio", "developed a feature swatch board from the mood board", "design");
    });
  },
  updateSwatch(pid: string, id: string, patch: Partial<{ name: string; hex: string; material: string; note: string }>) {
    patchProject(pid, (p) => ({ ...p, moodboard: { images: p.moodboard?.images ?? [], swatches: (p.moodboard?.swatches ?? []).map((s) => s.id === id ? { ...s, ...patch } : s) } }));
  },
  removeSwatch(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, moodboard: { images: p.moodboard?.images ?? [], swatches: (p.moodboard?.swatches ?? []).filter((s) => s.id !== id) } }));
  },

  /* ---- drawing reviews ---- */
  createReview(pid: string, title: string): string {
    const id = uid("rv-");
    patchProject(pid, (p) => logActivity({ ...p, reviews: [{ id, title, createdOn: realToday(), status: "open", sharedWithClient: false, access: [], sheets: [] }, ...(p.reviews ?? [])] }, "Studio", `opened a drawing review — ${title}`, "design"));
    return id;
  },
  addReviewSheet(pid: string, rid: string, sheet: { code: string; title: string; discipline: string; url?: string; summary?: string }) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: [...r.sheets, { id: uid("rs-"), code: sheet.code, title: sheet.title, discipline: sheet.discipline, revision: "P1", hue: [28, 40, 150, 205, 100][r.sheets.length % 5], remarks: [], url: sheet.url, summary: sheet.summary?.trim() || undefined }] } : r) }));
  },
  /** Set/replace the current drawing image (and its summary) on a sheet without
   *  issuing a new revision — for the first upload or a straight correction. */
  setSheetDrawing(pid: string, rid: string, sheetId: string, opts: { url?: string; summary?: string }) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, url: opts.url ?? s.url, summary: opts.summary !== undefined ? (opts.summary.trim() || undefined) : s.summary } : s) } : r) }));
  },
  /** Issue a revised sheet: bump the revision, carry the new drawing + a summary
   *  of what changed, and notify the client if the review is shared. */
  uploadRevision(pid: string, rid: string, sheetId: string, opts: { url?: string; summary?: string } = {}) {
    patchProject(pid, (p) => {
      const review = (p.reviews ?? []).find((r) => r.id === rid);
      const sheet = review?.sheets.find((s) => s.id === sheetId);
      const nextRev = bumpRev(sheet?.revision ?? "P1");
      const np = { ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, revision: nextRev, hue: opts.url ? s.hue : (s.hue + 40) % 360, url: opts.url ?? s.url, summary: opts.summary?.trim() || s.summary, revisedOn: nowISO() } : s) } : r) };
      const withNotice = review?.sharedWithClient
        ? { ...np, notices: [{ id: uid(), on: nowISO(), kind: "design" as const, title: `${sheet?.code} reissued — rev ${nextRev}`, body: opts.summary?.trim() || "A revised drawing is ready to review." }, ...(np.notices ?? [])].slice(0, 40) }
        : np;
      return logActivity(withNotice, "Studio", `issued ${sheet?.code} rev ${nextRev}`, "design");
    });
  },
  addRemark(pid: string, rid: string, sheetId: string, author: "client" | "team", authorName: string, body: string, attachmentUrl?: string) {
    patchProject(pid, (p) => {
      const rev = (p.reviews ?? []).find((r) => r.id === rid)?.sheets.find((s) => s.id === sheetId)?.revision ?? "P1";
      const np = { ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, summary: undefined, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, remarks: [...s.remarks, { id: uid(), author, authorName, body, createdOn: nowISO(), resolved: false, onRevision: rev, attachmentUrl }] } : s) } : r) };
      // a client change request should reach the studio inbox
      if (author === "client") {
        const sheet = (p.reviews ?? []).find((r) => r.id === rid)?.sheets.find((s) => s.id === sheetId);
        void notify({ to: STUDIO.email, subject: `${authorName} requested a change — ${p.name}`, heading: "New change request", body: `${authorName} left a remark on ${sheet?.code ?? "a drawing"} (${p.name}): "${body}"`, cta: { label: "Open the review", url: `https://${STUDIO.domain}/studio/${p.id}/review` } });
      }
      return logActivity(np, authorName, "left a remark on a drawing", "feedback");
    });
  },
  resolveRemark(pid: string, rid: string, sheetId: string, remarkId: string) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, remarks: s.remarks.map((m) => m.id === remarkId ? { ...m, resolved: !m.resolved } : m) } : s) } : r) }));
  },

  /* --- project packages: share a bundle of deliverables on a tokened link -- */
  createPackage(pid: string, data: {
    title: string; audience: "client" | "consultant" | "contractor";
    recipientName?: string; recipientEmail?: string; deliverableIds: string[]; note?: string;
  }, by: string): import("./types").ProjectPackage {
    const token = safeUUID().replace(/-/g, "") + safeUUID().replace(/-/g, "").slice(0, 8);
    let created = { id: uid("pkg-"), ref: "PKG-000", token, sharedBy: by, sharedAt: nowISO(), clientNotes: [], ...data } as import("./types").ProjectPackage;
    patchProject(pid, (p) => {
      const n = (p.packages ?? []).length ? Math.max(...(p.packages ?? []).map((s) => parseInt(s.ref.split("-")[1], 10) || 0)) + 1 : 1;
      created = { ...created, ref: `PKG-${String(n).padStart(3, "0")}` };
      const link = `https://${STUDIO.domain}/pkg/${token}`;
      if (data.recipientEmail) {
        void notify({
          to: data.recipientEmail,
          subject: `Drawings shared — ${data.title} (${p.code})`,
          heading: "A drawing package is ready",
          body: `${STUDIO.name} has shared ${data.deliverableIds.length} drawing${data.deliverableIds.length === 1 ? "" : "s"} with you${data.audience === "client" ? " for your review" : ""}. Open the package to view, download${data.audience === "client" ? " and leave any comments" : ""}.`,
          cta: { label: "Open the package", url: link },
        });
      }
      return logActivity({ ...p, packages: [created, ...(p.packages ?? [])] }, by, `shared ${created.ref} — ${data.title}`, "design");
    });
    return created;
  },
  removePackage(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, packages: (p.packages ?? []).filter((s) => s.id !== id) }));
  },
  resolvePackageNote(pid: string, pkgId: string, noteId: string, resolved: boolean) {
    patchProject(pid, (p) => ({
      ...p, packages: (p.packages ?? []).map((s) => s.id !== pkgId ? s
        : { ...s, clientNotes: (s.clientNotes ?? []).map((n) => n.id === noteId ? { ...n, resolved, resolvedAt: resolved ? nowISO() : undefined } : n) }),
    }));
  },
  /** A signed-in client confirms receipt of a shared package (the emailed public
   *  page posts through /api/pkg/:token/receipt instead). Closes the transmittal. */
  acknowledgePackage(pid: string, pkgId: string, by: string) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((s) => s.id === pkgId);
      const receipt = { by, at: nowISO() };
      const np = { ...p, packages: (p.packages ?? []).map((s) => s.id === pkgId ? { ...s, receipt, viewedAt: receipt.at } : s) };
      void notify({ to: STUDIO.email, subject: `Receipt confirmed — ${pkg?.ref ?? "package"} (${p.name})`, heading: "Package receipt confirmed", body: `${by} confirmed receipt of ${pkg?.ref ?? "a package"} ${pkg?.title ?? ""}.`, cta: { label: "Open the drawings", url: `https://${STUDIO.domain}/studio/${p.id}/drawings` } });
      return logActivity(np, by, `confirmed receipt of ${pkg?.ref ?? "a package"}`, "feedback");
    });
  },
  /** A signed-in client adds a revision comment on a shared package (the emailed
   *  public page posts through /api/pkg/:token/note instead). */
  addPackageNote(pid: string, pkgId: string, data: { code?: string; text: string; by: string }) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((s) => s.id === pkgId);
      const note = { id: uid("pn-"), code: data.code?.trim() || undefined, text: data.text.trim(), by: data.by, at: nowISO() };
      const np = { ...p, packages: (p.packages ?? []).map((s) => s.id === pkgId ? { ...s, clientNotes: [...(s.clientNotes ?? []), note] } : s) };
      void notify({ to: STUDIO.email, subject: `Client comment — ${pkg?.ref ?? "package"} (${p.name})`, heading: "New revision comment", body: `${data.by} commented on ${pkg?.title ?? "a package"}${note.code ? ` (${note.code})` : ""}: "${note.text}"`, cta: { label: "Open the drawings", url: `https://${STUDIO.domain}/studio/${p.id}/drawings` } });
      return logActivity(np, data.by, `left a comment on ${pkg?.ref ?? "a package"}`, "feedback");
    });
  },
  /** Record the studio's response to a client change request: what changed, a
   *  markup photo of the changed area, and the revision it lands on. Resolves the
   *  request and tells the client (if the review is shared). */
  respondToRemark(pid: string, rid: string, sheetId: string, remarkId: string, resp: { body: string; url?: string; revision?: string }, by: string) {
    patchProject(pid, (p) => {
      const review = (p.reviews ?? []).find((r) => r.id === rid);
      const sheet = review?.sheets.find((s) => s.id === sheetId);
      const response: RemarkResponse = { body: resp.body.trim(), url: resp.url, revision: resp.revision || sheet?.revision, respondedOn: nowISO(), by };
      const np = { ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, remarks: s.remarks.map((m) => m.id === remarkId ? { ...m, resolved: true, response } : m) } : s) } : r) };
      const withNotice = review?.sharedWithClient
        ? { ...np, notices: [{ id: uid(), on: nowISO(), kind: "design" as const, title: `Change addressed — ${sheet?.code ?? "drawing"}`, body: response.body.slice(0, 140) }, ...(np.notices ?? [])].slice(0, 40) }
        : np;
      return logActivity(withNotice, by, `responded to a change request on ${sheet?.code ?? "a drawing"}`, "design");
    });
  },
  /** The client approves (or un-approves) the studio's revised implementation of a
   *  change request. Tells the studio. */
  acceptRemarkResponse(pid: string, rid: string, sheetId: string, remarkId: string, by: string, accept = true) {
    patchProject(pid, (p) => {
      const review = (p.reviews ?? []).find((r) => r.id === rid);
      const sheet = review?.sheets.find((s) => s.id === sheetId);
      const np = { ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sheets: r.sheets.map((s) => s.id === sheetId ? { ...s, remarks: s.remarks.map((m) => m.id === remarkId && m.response ? { ...m, response: { ...m.response, accepted: accept ? { by, on: nowISO() } : undefined } } : m) } : s) } : r) };
      if (accept) void notify({ to: STUDIO.email, subject: `Client approved a change — ${p.name}`, heading: "Revised implementation approved", body: `${by} approved the revised implementation on ${sheet?.code ?? "a drawing"} (${p.name}).`, cta: { label: "Open the review", url: `https://${STUDIO.domain}/studio/${p.id}/review` } });
      return logActivity(np, by, accept ? `approved a revised implementation on ${sheet?.code ?? "a drawing"}` : `withdrew approval on ${sheet?.code ?? "a drawing"}`, "feedback");
    });
  },
  shareReview(pid: string, rid: string, shared: boolean) {
    patchProject(pid, (p) => logActivity({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, sharedWithClient: shared } : r) }, "Studio", shared ? "shared a drawing review with the client" : "unshared a drawing review", "design"));
  },
  setReviewAccess(pid: string, rid: string, emails: string[]) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, access: emails } : r) }));
  },
  setReviewSummary(pid: string, rid: string, summary: string) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, summary, summaryOn: realToday() } : r) }));
  },
  closeReview(pid: string, rid: string) {
    patchProject(pid, (p) => ({ ...p, reviews: (p.reviews ?? []).map((r) => r.id === rid ? { ...r, status: r.status === "open" ? "closed" : "open" } : r) }));
  },

  /* ---- brief → design checklist + target/designed audit ---- */
  /** Turn the approved brief into the working checklist: one line per space
   *  requirement, one per substantive brief section. Regenerating keeps what's
   *  already been checked off or measured — the brief may grow; the record of
   *  what the design has answered doesn't reset. */
  generateBriefChecklist(pid: string, by: string) {
    patchProject(pid, (p) => {
      const prev = p.briefChecklist?.items ?? [];
      const keep = (match: (i: ChecklistItem) => boolean) => prev.find(match);
      const spaceItems: ChecklistItem[] = (p.brief.spaces ?? []).map((s) => {
        const old = keep((i) => i.spaceId === s.id);
        return {
          id: old?.id ?? uid("chk-"), kind: "space", spaceId: s.id,
          label: `${s.name}${s.qty > 1 ? ` ×${s.qty}` : ""}`,
          done: old?.done, doneBy: old?.doneBy, doneOn: old?.doneOn, note: old?.note,
          designedQty: old?.designedQty, designedArea: old?.designedArea,
        };
      });
      const reqItems: ChecklistItem[] = p.brief.sections
        .filter((s) => s.body.trim().length > 20)
        .map((s) => {
          const old = keep((i) => i.sectionId === s.id);
          return { id: old?.id ?? uid("chk-"), kind: "requirement", sectionId: s.id, label: s.title, done: old?.done, doneBy: old?.doneBy, doneOn: old?.doneOn, note: old?.note };
        });
      const checklist: BriefChecklist = {
        generatedOn: p.briefChecklist?.generatedOn ?? realToday(), updatedOn: realToday(),
        sharedWithClient: p.briefChecklist?.sharedWithClient,
        items: [...spaceItems, ...reqItems],
      };
      return logActivity({ ...p, briefChecklist: checklist }, by, "built the design checklist from the brief", "brief");
    });
  },
  toggleChecklistItem(pid: string, itemId: string, done: boolean, by: string) {
    patchProject(pid, (p) => ({
      ...p,
      briefChecklist: p.briefChecklist && {
        ...p.briefChecklist, updatedOn: realToday(),
        items: p.briefChecklist.items.map((i) => i.id === itemId
          ? { ...i, done, doneBy: done ? by : undefined, doneOn: done ? realToday() : undefined } : i),
      },
    }));
  },
  setChecklistItem(pid: string, itemId: string, patch: Partial<Pick<ChecklistItem, "designedQty" | "designedArea" | "note">>) {
    patchProject(pid, (p) => ({
      ...p,
      briefChecklist: p.briefChecklist && {
        ...p.briefChecklist, updatedOn: realToday(),
        items: p.briefChecklist.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
      },
    }));
  },
  shareBriefChecklist(pid: string, on: boolean) {
    patchProject(pid, (p) => {
      const np = { ...p, briefChecklist: p.briefChecklist && { ...p.briefChecklist, sharedWithClient: on } };
      if (!on) return np;
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: "Your brief, tracked against the design", body: "Every requirement and target space from your brief now shows how the design answers it — including a target vs designed area report." };
      return logActivity({ ...np, notices: [notice, ...(np.notices ?? [])].slice(0, 40) }, "Studio", "shared the design checklist with the client", "brief");
    });
  },

  /* ---- formal stage submissions ---- */
  /** Close a design stage with a formal submission: freeze every current sheet,
   *  mint a public package the client can view / download / share, and tell
   *  them. The record of what the stage concluded with. */
  submitStage(pid: string, data: { stage: import("./types").StageKey; title: string; note?: string }, by: string): string {
    let ref = "";
    patchProject(pid, (p) => {
      const drawn = (p.deliverables ?? []).filter((d) => !d.archived && (d.previewUrl || d.fileUrl));
      if (!drawn.length) return p;
      const n = (p.submissions ?? []).length ? Math.max(...(p.submissions ?? []).map((s) => parseInt(s.ref.split("-")[1], 10) || 0)) + 1 : 1;
      ref = `SUB-${String(n).padStart(3, "0")}`;
      const token = safeUUID().replace(/-/g, "") + safeUUID().replace(/-/g, "").slice(0, 8);
      const pkg: import("./types").ProjectPackage = {
        id: uid("pkg-"), ref: `PKG-S${String(n).padStart(2, "0")}`, token,
        title: data.title.trim(), audience: "client",
        recipientName: p.client.name, recipientEmail: p.client.email,
        deliverableIds: drawn.map((d) => d.id), note: data.note?.trim() || undefined,
        sharedBy: by, sharedAt: nowISO(), clientNotes: [],
      };
      const submission: import("./types").StageSubmission = {
        id: uid("sub-"), ref, stage: data.stage, title: data.title.trim(), note: data.note?.trim() || undefined,
        submittedOn: realToday(), by, packageToken: token,
        sheets: drawn.map((d) => ({ code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, revision: d.revision || "P1", building: d.building, previewUrl: d.previewUrl, fileUrl: d.fileUrl, page: d.page })),
      };
      const notice: ClientNotice = { id: uid(), on: nowISO(), kind: "design", title: `Stage submission — ${submission.title}`, body: `${submission.sheets.length} sheets submitted in full. View, download or share the complete package from your portal.` };
      void notify({
        to: p.client.email,
        subject: `Stage submission — ${submission.title} (${p.name})`,
        heading: "A design stage has been submitted",
        body: `${STUDIO.name} has completed and formally submitted ${submission.title} on ${p.name}: ${submission.sheets.length} sheets, provided in full. Open it to view, download or share the package.`,
        cta: { label: "Open the submission", url: `https://${STUDIO.domain}/pkg/${token}` },
      });
      return logActivity(
        { ...p, submissions: [submission, ...(p.submissions ?? [])], packages: [pkg, ...(p.packages ?? [])], notices: [notice, ...(p.notices ?? [])].slice(0, 40) },
        by, `submitted ${submission.title} — ${ref} (${submission.sheets.length} sheets)`, "design");
    });
    return ref;
  },

  markSubmissionViewed(pid: string, sid: string) {
    patchProject(pid, (p) => ({
      ...p,
      submissions: (p.submissions ?? []).map((s) => s.id === sid && !s.clientViewedOn ? { ...s, clientViewedOn: realToday() } : s),
    }));
  },

  /* ---- partner submissions & cost proposals: the studio's answers ---- */
  respondPartnerSubmission(pid: string, sid: string, data: { status: "approved" | "revise"; response: string }, by: string) {
    patchProject(pid, (p) => {
      const sub = (p.partnerSubmissions ?? []).find((s) => s.id === sid);
      const ptr = (p.partners ?? []).find((x) => x.id === sub?.partnerId);
      if (ptr?.email) void notify({ to: ptr.email, subject: `${sub?.ref} ${data.status === "approved" ? "approved" : "needs revision"} — ${p.name}`, heading: `Your submission ${data.status === "approved" ? "is approved" : "needs revision"}`, body: `${by} on "${sub?.title}": ${data.response}`, cta: { label: "Open your workspace", url: `https://${STUDIO.domain}/partner/${ptr.token}` } });
      return logActivity({
        ...p,
        partnerSubmissions: (p.partnerSubmissions ?? []).map((s) => s.id === sid ? { ...s, status: data.status, response: data.response.trim(), respondedBy: by, respondedOn: nowISO() } : s),
      }, by, `${data.status === "approved" ? "approved" : "returned"} ${sub?.ref ?? "a partner submission"}`, "design");
    });
  },
  respondPartnerProposal(pid: string, prid: string, data: { status: "accepted" | "declined"; response: string }, by: string) {
    patchProject(pid, (p) => {
      const pr = (p.partnerProposals ?? []).find((s) => s.id === prid);
      const ptr = (p.partners ?? []).find((x) => x.id === pr?.partnerId);
      if (ptr?.email) void notify({ to: ptr.email, subject: `${pr?.ref} ${data.status} — ${p.name}`, heading: `Your cost proposal was ${data.status}`, body: `${by} on "${pr?.title}": ${data.response}`, cta: { label: "Open your workspace", url: `https://${STUDIO.domain}/partner/${ptr.token}` } });
      return logActivity({
        ...p,
        partnerProposals: (p.partnerProposals ?? []).map((s) => s.id === prid ? { ...s, status: data.status, response: data.response.trim(), respondedBy: by, respondedOn: nowISO() } : s),
      }, by, `${data.status} ${pr?.ref ?? "a cost proposal"}`, "design");
    });
  },

  /* ---- RFIs (construction-phase requests for information) ---- */
  createRfi(pid: string, data: { subject: string; question: string; discipline?: string; sheetCode?: string; dueOn?: string; attachmentUrl?: string; by: string; role: "studio" | "client" }): string {
    const id = uid("rfi-");
    patchProject(pid, (p) => {
      const n = (p.rfis ?? []).length ? Math.max(...(p.rfis ?? []).map((r) => parseInt(r.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const rfi: import("./types").RFI = {
        id, ref: `RFI-${String(n).padStart(3, "0")}`, subject: data.subject.trim(), question: data.question.trim(),
        discipline: data.discipline, sheetCode: data.sheetCode?.trim() || undefined, status: "open",
        ballInCourt: data.role === "client" ? "studio" : "client", raisedBy: data.by, raisedByRole: data.role,
        raisedOn: nowISO(), dueOn: data.dueOn, attachmentUrl: data.attachmentUrl,
      };
      const to = data.role === "client" ? STUDIO.email : p.client.email;
      void notify({ to, subject: `${rfi.ref} — ${rfi.subject} (${p.name})`, heading: `New RFI ${rfi.ref}`, body: `${data.by} raised an RFI on ${p.name}: "${rfi.subject}". ${rfi.question.slice(0, 160)}`, cta: { label: "Open the RFI", url: `https://${STUDIO.domain}/${data.role === "client" ? "studio" : "portal"}/${p.id}${data.role === "client" ? "/rfis" : ""}` } });
      return logActivity({ ...p, rfis: [rfi, ...(p.rfis ?? [])] }, data.by, `raised ${rfi.ref} — ${rfi.subject}`, "design");
    });
    return id;
  },
  answerRfi(pid: string, rid: string, answer: string, by: string) {
    patchProject(pid, (p) => {
      const rfi = (p.rfis ?? []).find((r) => r.id === rid);
      const np = { ...p, rfis: (p.rfis ?? []).map((r) => r.id === rid ? { ...r, answer: answer.trim(), answeredBy: by, answeredOn: nowISO(), status: "answered" as const, ballInCourt: (r.raisedByRole === "client" ? "client" : "studio") as import("./types").BallInCourt } : r) };
      void notify({ to: rfi?.raisedByRole === "client" ? p.client.email : STUDIO.email, subject: `${rfi?.ref ?? "RFI"} answered — ${p.name}`, heading: `${rfi?.ref ?? "RFI"} answered`, body: `${by} answered ${rfi?.ref ?? "an RFI"}: "${answer.slice(0, 180)}"`, cta: { label: "Open the RFI", url: `https://${STUDIO.domain}/studio/${p.id}/rfis` } });
      return logActivity(np, by, `answered ${rfi?.ref ?? "an RFI"}`, "design");
    });
  },
  setRfiStatus(pid: string, rid: string, status: import("./types").RfiStatus) {
    patchProject(pid, (p) => ({ ...p, rfis: (p.rfis ?? []).map((r) => r.id === rid ? { ...r, status } : r) }));
  },
  removeRfi(pid: string, rid: string) {
    patchProject(pid, (p) => ({ ...p, rfis: (p.rfis ?? []).filter((r) => r.id !== rid) }));
  },

  /* ---- variations / change orders ---- */
  createVariation(pid: string, data: { title: string; description: string; reason?: string; amount: number; sourceReviewId?: string; sheetCodes?: string[]; by: string }): string {
    const id = uid("vo-");
    patchProject(pid, (p) => {
      const n = (p.variations ?? []).length ? Math.max(...(p.variations ?? []).map((v) => parseInt(v.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const v: import("./types").Variation = {
        id, ref: `VO-${String(n).padStart(3, "0")}`, title: data.title.trim(), description: data.description.trim(),
        reason: data.reason?.trim() || undefined, amount: Math.round(data.amount) || 0, status: "draft",
        createdOn: realToday(), sourceReviewId: data.sourceReviewId, sheetCodes: data.sheetCodes,
      };
      return logActivity({ ...p, variations: [v, ...(p.variations ?? [])] }, data.by, `drafted ${v.ref} — ${v.title}`, "payment");
    });
    return id;
  },
  updateVariation(pid: string, vid: string, patch: Partial<Pick<import("./types").Variation, "title" | "description" | "reason" | "amount">>) {
    patchProject(pid, (p) => ({ ...p, variations: (p.variations ?? []).map((v) => v.id === vid ? { ...v, ...patch, amount: patch.amount !== undefined ? Math.round(patch.amount) || 0 : v.amount } : v) }));
  },
  issueVariation(pid: string, vid: string, by: string) {
    patchProject(pid, (p) => {
      const v = (p.variations ?? []).find((x) => x.id === vid);
      const np = { ...p, variations: (p.variations ?? []).map((x) => x.id === vid ? { ...x, status: "issued" as const, issuedOn: realToday() } : x) };
      const withNotice = { ...np, notices: [{ id: uid(), on: nowISO(), kind: "payment" as const, title: `Variation ${v?.ref ?? ""} issued for approval`, body: `${v?.title ?? ""} — ${money(v?.amount ?? 0, p.billing?.currency ?? p.currency)}` }, ...(np.notices ?? [])].slice(0, 40) };
      void notify({ to: p.client.email, subject: `Variation ${v?.ref ?? ""} for approval — ${p.name}`, heading: `Variation ${v?.ref ?? ""}`, body: `${STUDIO.name} has issued a variation for your approval: ${v?.title ?? ""} — ${money(v?.amount ?? 0, p.billing?.currency ?? p.currency)}.`, cta: { label: "Review the variation", url: `https://${STUDIO.domain}/portal/${p.id}/billing` } });
      return logActivity(withNotice, by, `issued ${v?.ref ?? "a variation"} for approval`, "payment");
    });
  },
  decideVariation(pid: string, vid: string, approve: boolean, by: string, note?: string) {
    patchProject(pid, (p) => {
      const v = (p.variations ?? []).find((x) => x.id === vid);
      const np = { ...p, variations: (p.variations ?? []).map((x) => x.id === vid ? { ...x, status: (approve ? "approved" : "rejected") as import("./types").VariationStatus, decidedBy: by, decidedOn: nowISO(), decisionNote: note?.trim() || undefined } : x) };
      void notify({ to: STUDIO.email, subject: `Variation ${v?.ref ?? ""} ${approve ? "approved" : "rejected"} — ${p.name}`, heading: `Variation ${approve ? "approved" : "rejected"}`, body: `${by} ${approve ? "approved" : "rejected"} ${v?.ref ?? "a variation"} (${v?.title ?? ""}).`, cta: { label: "Open billing", url: `https://${STUDIO.domain}/studio/${p.id}/billing` } });
      return logActivity(np, by, `${approve ? "approved" : "rejected"} ${v?.ref ?? "a variation"}`, "payment");
    });
  },
  /** Bill an approved variation as an invoice (taxed like any other). */
  billVariation(pid: string, vid: string, by: string) {
    patchProject(pid, (p) => {
      const b = p.billing; const v = (p.variations ?? []).find((x) => x.id === vid);
      if (!v || v.status !== "approved" || v.invoiceId) return p;
      const seq = String(p.invoices.length + 1).padStart(2, "0");
      const tail = p.code.split("·").pop() ?? "0001";
      const issuedOn = nowISO().slice(0, 10);
      const dueOn = new Date(Date.now() + ((b?.paymentTermsDays ?? 14)) * 86_400_000).toISOString().slice(0, 10);
      const inv: Invoice = { id: uid("inv-"), ref: `INV-${tail}-${seq}`, title: `Variation ${v.ref} — ${v.title}`, ...(b ? taxed(v.amount, b) : { amount: v.amount }), issuedOn, dueOn, status: "due" };
      void notify({ to: p.client.email, subject: `Invoice ${inv.ref} — variation ${v.ref} (${p.name})`, heading: `Invoice ${inv.ref}`, body: `${v.title} — ${money(inv.amount, b?.currency ?? p.currency)}, due ${dueOn}.`, cta: { label: "Open billing", url: `https://${STUDIO.domain}/portal/${p.id}/billing` } });
      return logActivity({ ...p, invoices: [inv, ...p.invoices], variations: (p.variations ?? []).map((x) => x.id === vid ? { ...x, invoiceId: inv.id } : x) }, by, `billed ${v.ref} as ${inv.ref}`, "payment");
    });
  },
  removeVariation(pid: string, vid: string) {
    patchProject(pid, (p) => ({ ...p, variations: (p.variations ?? []).filter((v) => v.id !== vid) }));
  },

  /* ---- site progress log ---- */
  addSiteUpdate(pid: string, data: { title: string; note?: string; zone?: string; percent?: number; photos: string[]; by: string }) {
    patchProject(pid, (p) => {
      const u: import("./types").SiteUpdate = { id: uid("su-"), on: nowISO(), by: data.by, title: data.title.trim(), note: data.note?.trim() || undefined, zone: data.zone?.trim() || undefined, percent: typeof data.percent === "number" ? Math.max(0, Math.min(100, Math.round(data.percent))) : undefined, photos: data.photos };
      const withNotice = { ...p, siteLog: [u, ...(p.siteLog ?? [])], notices: [{ id: uid(), on: nowISO(), kind: "design" as const, title: `Site update — ${u.title}`, body: u.note?.slice(0, 140) ?? (u.percent != null ? `${u.percent}% complete${u.zone ? ` · ${u.zone}` : ""}` : "") }, ...(p.notices ?? [])].slice(0, 40) };
      return logActivity(withNotice, data.by, `posted a site update — ${u.title}`, "design");
    });
  },
  removeSiteUpdate(pid: string, sid: string) {
    patchProject(pid, (p) => ({ ...p, siteLog: (p.siteLog ?? []).filter((u) => u.id !== sid) }));
  },

  /* ---- online payment links ---- */
  setInvoicePayUrl(pid: string, invId: string, url: string) {
    patchProject(pid, (p) => ({ ...p, invoices: p.invoices.map((i) => i.id === invId ? { ...i, payUrl: url.trim() || undefined } : i) }));
  },
  setBillingPayUrl(pid: string, url: string) {
    patchProject(pid, (p) => p.billing ? { ...p, billing: { ...p.billing, payUrl: url.trim() || undefined } } : p);
  },

  /* ---- BOQ + QA report (AI outputs the studio can edit) ---- */
  setBoq(pid: string, items: import("./types").BoqItem[], by: string) {
    patchProject(pid, (p) => logActivity({ ...p, boq: items }, by, `updated the bill of quantities (${items.length} items)`, "design"));
  },
  updateBoqItem(pid: string, iid: string, patch: Partial<import("./types").BoqItem>) {
    patchProject(pid, (p) => ({ ...p, boq: (p.boq ?? []).map((i) => i.id === iid ? { ...i, ...patch } : i) }));
  },
  addBoqItem(pid: string, item: Omit<import("./types").BoqItem, "id">) {
    patchProject(pid, (p) => ({ ...p, boq: [...(p.boq ?? []), { ...item, id: uid("bq-") }] }));
  },
  removeBoqItem(pid: string, iid: string) {
    patchProject(pid, (p) => ({ ...p, boq: (p.boq ?? []).filter((i) => i.id !== iid) }));
  },
  setQaReport(pid: string, report: import("./types").QaReport) {
    patchProject(pid, (p) => ({ ...p, qa: report }));
  },

  /* ---- estimator ---- */
  /* A locked configuration is the agreed one. Refusing the write here rather
     than only hiding the controls means a stale tab can't quietly reopen it. */
  setEstimate(pid: string, config: EstimateConfig) {
    patchProject(pid, (p) => (p.feeLock ? p : followFee({ ...p, estimate: config })));
  },
  /** Rebuild a stage-built payment schedule from the current fee, keeping any
   *  milestone already billed exactly as it was. */
  rebuildStageSchedule(pid: string, by: string) {
    patchProject(pid, (p) => {
      const next = refreshStageSchedule(p, state.rateCard);
      if (!next) return p;
      const billed = (p.billing?.milestones ?? []).filter((m) => m.status !== "scheduled");
      return logActivity({ ...p, billing: next }, by,
        `rebuilt the payment schedule from the current fee — ${money(next.contractSum, next.currency)}${billed.length ? ` (${billed.length} billed milestone${billed.length === 1 ? "" : "s"} kept)` : ""}`, "payment");
    });
  },
  setFeeLock(pid: string, on: boolean, by: string, note?: string) {
    patchProject(pid, (p) => logActivity(
      { ...p, feeLock: on ? { on: realToday(), by, note: note?.trim() || undefined } : undefined },
      by,
      on ? "locked the fee configuration — the client can see it but no longer change it" : "unlocked the fee configuration",
      "payment"));
  },
  /** Turn a client-portal feature on or off. `on` = visible to the client
   *  (default); off adds the tab key to portalHidden so it's hidden. */
  setPortalFeature(pid: string, key: string, on: boolean) {
    patchProject(pid, (p) => {
      const hidden = new Set(p.portalHidden ?? []);
      if (on) hidden.delete(key); else hidden.add(key);
      return { ...p, portalHidden: [...hidden] };
    });
  },
  /** Curate where a room sits in the client's portal. "auto" hands it back to the
   *  project's own judgement, which is the right default for most of them. */
  setPortalPlacement(pid: string, key: string, place: "front" | "menu" | "off" | "auto") {
    patchProject(pid, (p) => {
      const hidden = new Set(p.portalHidden ?? []);
      const pinned = new Set(p.portalPinned ?? []);
      const tucked = new Set(p.portalTucked ?? []);
      hidden.delete(key); pinned.delete(key); tucked.delete(key);
      if (place === "off") hidden.add(key);
      else if (place === "front") pinned.add(key);
      else if (place === "menu") tucked.add(key);
      return { ...p, portalHidden: [...hidden], portalPinned: [...pinned], portalTucked: [...tucked] };
    });
  },
  resetPortalCuration(pid: string) {
    patchProject(pid, (p) => ({ ...p, portalHidden: [], portalPinned: [], portalTucked: [] }));
  },
  saveQuoteFromEstimate(pid: string, who: string) {
    patchProject(pid, (p) => {
      if (!p.estimate) return p;
      const est = computeEstimate(p.estimate, state.rateCard);
      const n = p.quotations.length + 1;
      const lines = est.lines.map((l) => ({ id: uid(), label: l.label, qty: 1, unit: l.kind === "addon" ? "service" : "discipline", rate: l.amount }));
      if (est.discount > 0) lines.push({ id: uid(), label: `Discount (${est.discountPct}%)`, qty: 1, unit: "credit", rate: -est.discount });
      const disb = est.phases.map((ph) => `${ph.label}: ${money(ph.amount, p.estimate!.currency)} (${ph.pctLabel}%)`).join(" · ");
      const q: Quotation = {
        id: uid("q-"), ref: `Q-${p.code.slice(-3)}-${String.fromCharCode(64 + n)}`,
        title: "Design Services — Configured Scope", issuedOn: realToday(), status: "draft", taxRate: 0.14,
        note: `${(est.pctOfBudget * 100).toFixed(1)}% of a ${money(p.estimate.budget, p.estimate.currency)} budget. Disbursement — ${disb}. Paid during construction: ${money(est.constructionAmount, p.estimate.currency)}.`,
        lines,
      };
      return logActivity({ ...p, quotations: [q, ...p.quotations] }, who, "generated a quotation from the scope configurator", "payment");
    });
  },

  /* ---- team ---- */
  inviteTeam(name: string, email: string, role: TeamRole) {
    const m: TeamMember = { id: uid("t-"), name, email, role, code: makeCode(name, "STUDIO"), status: "invited", avatarHue: [24, 150, 205, 15, 100][state.team.length % 5] };
    commit({ ...state, team: [...state.team, m] });
  },
  setTeamRole(id: string, role: TeamRole) {
    commit({ ...state, team: state.team.map((m) => m.id === id ? { ...m, role } : m) });
  },
  reissueTeamCode(id: string) {
    commit({ ...state, team: state.team.map((m) => m.id === id ? { ...m, code: makeCode(m.name, "STUDIO") } : m) });
  },
  removeTeam(id: string) {
    commit({ ...state, team: state.team.filter((m) => m.id !== id) });
  },
  /* ---- rate card ---- */
  updateDiscipline(key: string, patch: Partial<{ pct: number; floor: number }>) {
    commit({ ...state, rateCard: { ...state.rateCard, disciplines: state.rateCard.disciplines.map((d) => d.key === key ? { ...d, ...patch } : d) } });
  },
  updateAddon(key: string, value: number) {
    commit({ ...state, rateCard: { ...state.rateCard, addons: state.rateCard.addons.map((a) => a.key === key ? { ...a, value } : a) } });
  },
  updateComplexity(key: string, mult: number) {
    commit({ ...state, rateCard: { ...state.rateCard, complexity: state.rateCard.complexity.map((c) => c.key === key ? { ...c, mult } : c) } });
  },
  updatePhase(key: string, pct: number) {
    commit({ ...state, rateCard: { ...state.rateCard, phases: state.rateCard.phases.map((ph) => ph.key === key ? { ...ph, pct } : ph) } });
  },
  /* ---- additional (out-of-scope) services ---- */
  addService() {
    const svc: AdditionalService = { key: "svc-" + safeUUID().slice(0, 8), label: "New service", blurb: "", unit: "item", rate: 0 };
    commit({ ...state, rateCard: { ...state.rateCard, services: [...(state.rateCard.services ?? []), svc] } });
  },
  updateService(key: string, patch: Partial<AdditionalService>) {
    commit({ ...state, rateCard: { ...state.rateCard, services: (state.rateCard.services ?? []).map((s) => s.key === key ? { ...s, ...patch } : s) } });
  },
  removeService(key: string) {
    commit({ ...state, rateCard: { ...state.rateCard, services: (state.rateCard.services ?? []).filter((s) => s.key !== key) } });
  },
  resetRateCard() { commit({ ...state, rateCard: DEFAULT_RATECARD }); },

  setRolePermission(role: RoleKey, cap: Capability, value: boolean) {
    if (role === "admin") return; // admin is always full-access
    commit({ ...state, roleMatrix: { ...state.roleMatrix, [role]: { ...state.roleMatrix[role], [cap]: value } } });
  },
  resetRoleMatrix() {
    commit({ ...state, roleMatrix: DEFAULT_MATRIX });
  },
};

/* Cryptographically-random, non-guessable access code (unambiguous alphabet). */
function makeCode(from: string, prefix?: string): string {
  const alpha = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I
  const pfx = (prefix || from.replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase() || "ATL");
  const taken = new Set<string>([
    ...state.projects.map((p) => p.client.code),
    ...state.team.map((m) => m.code),
  ]);
  for (let tries = 0; tries < 50; tries++) {
    const rnd = crypto.getRandomValues(new Uint8Array(8));
    let s = ""; for (const b of rnd) s += alpha[b % alpha.length];
    const code = `${pfx}-${s}`;
    if (!taken.has(code)) return code;
  }
  return `${pfx}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

/* ------------------------------------------------------------- selectors */
export function money(n: number, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n);
}
export function quoteTotal(q: Quotation) {
  const sub = q.lines.reduce((s, l) => s + l.qty * l.rate, 0);
  return { sub, tax: sub * q.taxRate, total: sub * (1 + q.taxRate) };
}
/** What the client actually owes: issued invoices less the money received
    against them. An invoice half paid owes half, not all of it. */
export function outstanding(p: Project) {
  return p.invoices
    .filter((i) => i.status === "due" || i.status === "overdue")
    .reduce((s, i) => s + Math.max(0, i.amount - confirmedPaid(i)), 0);
}
export function currentStage(p: Project) {
  return p.stages.find((s) => s.status === "active") ?? p.stages[p.stages.length - 1];
}
export function stageProgress(p: Project) {
  const done = p.stages.filter((s) => s.status === "done").length;
  const active = p.stages.some((s) => s.status === "active") ? 0.5 : 0;
  return Math.round(((done + active) / p.stages.length) * 100);
}
